<template>
    <div id="project-navigator-container" ref="ProjectTree">
        <div id="app-layout-right-column__content__title">
            <span>Media</span>
        </div>

        <ProjectTreeSearchAndFilters />

        <div id="project-root-container">
            <span class="project-title">{{ project.name }}</span>
            <i
                v-if="!isGuest"
                class="material-icons"
                :class="{ disabled: !canCreateBin }"
                @click="canCreateBin ? createBin() : null"
                content="Create Bin"
                v-tippy
            >
                create_new_folder
            </i>
        </div>

        <input type="file" @change="startImportProcess" id="import-media" multiple />

        <div class="scrollable-con">
            <span v-if="isGuest && projectTree.length == 0" class="d-block" style="font-size: 14px; line-height: 1.3; padding: 0 20px 0 6px">No media assets have been shared with you just yet. Please ask the owner of this project to share some assets with you or check back later.</span>
            <v-treeview
                id="project-tree"
                :class="{'--guest': isGuest}"
                v-model="selectedFiles"
                @input="onSelectingFiles"
                :items="projectTree"
                :search="search"
                :filter="filter"
                @update:open="onOpenProjectTree"
                :open="openProjectTree"
                dense
                open-on-click
                selectable
                :selection-type="'independent'"
                item-key="key"
                return-object
                ref="projectTree"
            >
                <template v-slot:prepend="{ item, open }">
                    <div
                        v-if="['image', 'match', 'candidate'].includes(item.type)"
                        class="thumbnail-container"
                        :data-thumbnail-container="item.id"
                        :class="{
                        '--edited': item.IDT && !item.IDT.initial,
                        '--selected': activeImage == item.id,
                        '--notify': item.type == 'candidate' && item.folder_slug == 'shared' && notificationsOfFile(item.parent_file_id).length > 0
                    }"
                    >
                        <v-lazy>
                            <Thumbnail :item="item" @click.native="onFileSelection($event, item.bin_id, item)" />
                        </v-lazy>
                    </div>

                    <i v-else class="material-icons">{{ open ? "folder_open" : "folder" }}</i>
                </template>

                <template v-slot:label="{ item }">
                    <div class="file"
                         file
                         :file_id="item.id"
                         :folder-slug="item.folder_slug"
                         :folder-id="item.folder_id"
                         :parent-file-id="item.parent_file_id"
                         v-if="['image', 'match', 'candidate'].includes(item.type)"
                    >
                        <v-text-field
                            v-if="item.key == renamingFileKey"
                            @keydown="renameFile"
                            @blur="endFileRenaming"
                            v-model="renamingFileName"
                            autofocus
                            label="File Name"
                            dark
                            flat
                            solo-inverted
                            hide-details
                        ></v-text-field>

                        <span
                            v-else-if="item.type == 'image'"
                            class="file-name"
                            :class="{ '--selected': activeImage == item.id }"
                            @click="onFileSelection($event, item.bin_id, item)"
                        >
						{{ nicename(item.name, true) }}
					</span>
                        <span
                            v-else-if="item.type == 'candidate'"
                            class="file-name"
                            :class="{ '--candidate': item.type == 'candidate', '--selected': activeCandidate == item.id }"
                            @click="onFileSelection($event, item.bin_id, item)"
                        >
						{{ nicename(item.name) }}
					</span>
                        <span
                            v-else-if="item.type == 'match'"
                            class="file-name"
                            :class="{ '--match': item.type == 'match', '--selected': activeMatch.id == item.id }"
                            @click="onFileSelection($event, item.bin_id, item)"
                        >
						{{ nicename(item.name) }}
					</span>

                        <i v-if="item.status == 'failed'" class="material-icons color-warning" v-tippy content="Failed to upload. Please upload again.">error</i>

                        <fileTags :file="item" />
                        <NewCommentNotificationsCounter v-if="item.type == 'image'"
                                                        :count="newCommentNotificationsOfFile(item.id).length"
                                                        class="ml-2"
                        />

                        <NewCommentNotificationsCounter v-if="item.type == 'candidate' && item.folder_slug == 'shared'"
                                                        :count="newCommentNotificationsOfFile(item.parent_file_id, 'shared').length"
                                                        class="ml-2"
                        />

						<v-icon v-if="item.type == 'candidate' && item.downloadable" @click="downloadDownloadableMedia(item.downloadable)" size="18" v-tippy="{placement: 'top', boundary: 'window'}" :content="getDownloadableMediaAsLabel(item.downloadable)" class="material-symbols-outlined ml-2">download</v-icon>
                    </div>

                    <v-text-field
                        v-else-if="item.id == renamingBinId"
                        @keydown="renameBin"
                        @blur="endBinRenaming"
                        v-model="renamingBinName"
                        autofocus
                        label="Bin Name"
                        dark
                        flat
                        solo-inverted
                        hide-details
                    ></v-text-field>

                    <div v-else
                         class="folder d-flex align-center"
                         folder
                         :folder_id="item.id"
                         :folder-slug="item.slug"
                    >
                        <span folder :folder_id="item.id">{{ item.name }}</span>
                        <NewCommentNotificationsCounter v-if="['bin', 'main'].includes(item.type)" :count="newCommentNotificationsOfBin(item.id).length"
                                                        class="ml-auto"
                        />
                        <NewCommentNotificationsCounter v-else-if="['media', 'shared'].includes(item.slug)" :count="newCommentNotificationsOfFolder(item.parent_id, item.slug).length"
                                                        class="ml-auto"
                                                        :style="item.slug == 'shared' ? 'margin-right: 18px' : ''"

                        />
                    </div>

                </template>

                <template v-slot:append="{ item }">
                    <i
                        class="material-icons import-images"
                        :class="{ disabled: !canUploadFiles }"
                        v-if="item.slug == 'media'"
                        @click.stop="canUploadFiles ? openImportDialog(item.id) : null"
                        :name="'importImageBtn' + item.id"
                    >
                        backup
<!--                        <tippy-->
<!--                            v-if="startTips == 1"-->
<!--                            placement="right"-->
<!--                            boundary="window"-->
<!--                            showOnInit-->
<!--                            theme="&#45;&#45;start"-->
<!--                            hideOnClick="false"-->
<!--                            interactive-->
<!--                            :onHide="-->
<!--							() => {-->
<!--								if (startTips == 1) return false;-->
<!--							}-->
<!--						"-->
<!--                            :to="'importImageBtn' + item.id"-->
<!--                        >-->
<!--                            <span class="step">1 of 5</span>-->
<!--                            <span class="title">Upload images or video files to get started</span>-->
<!--                            <span class="description">-->
<!--							Upload your images or video files to start creating your looks. You can begin colour grading even before all files have-->
<!--							finished uploading.-->
<!--						</span>-->
<!--                            <a href="https://fylm.ai/docs/introduction-to-folder-structure-in-fylm-ai" target="_blank">Learn-->
<!--                                more</a>-->
<!--                        </tippy>-->
                        <tippy
                            placement="top"
                            showOnInit="false"
                            theme=""
                            boundary="scrollParent"
                            hideOnClick="true"
                            :to="'importImageBtn' + item.id"
                        >
                            Upload Files
                        </tippy>
                    </i>

                    <div class="menu-container" v-if="['image', 'match', 'candidate'].includes(item.type)">
                        <v-menu left nudge-left="20px" :content-class="'file-options-container'" :disabled="blockLoading">
                            <template v-slot:activator="{ on, attrs }">
                                <i class="material-icons" v-bind="attrs" :file_id="item.id" v-on="on"
                                   @click="onFileOptionsOpen(item)"
                                   :class="{ disabled: blockLoading }"
                                >more_vert</i>
                            </template>
                            <v-list v-if="!item.archived_at && !item.trashed_at">
                                <v-menu
                                    v-if="item.type == 'image'"
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --rating --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on" :disabled="!canUseColorGradingTools">
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-symbols-outlined">ambient_screen</i>
                                            <v-list-item-title>NeuralToneAI <span class="control-label--new">NEW</span></v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="modelName in neuralToneAIModelNamesList"
                                            :key="modelName"
                                            :class="{ '--selected': false }"
                                            @click="neuralToneAIHandler(modelName, item)"
                                        >
                                            <v-list-item-title>{{ modelName }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    v-if="item.type == 'image'"
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --rating --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on" :disabled="!canUseColorGradingTools">
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-symbols-outlined">ambient_screen</i>
                                            <v-list-item-title>NeuralFilmAI <span class="control-label--new">NEW</span></v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="modelName in neuralFilmAIModelNamesList"
                                            :key="modelName"
                                            :class="{ '--selected': false }"
                                            @click="neuralFilmAIHandler(modelName, item)"
                                        >
                                            <v-list-item-title>{{ modelName }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    v-if="item.type == 'image'"
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --rating --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on" :disabled="!canUseColorGradingTools">
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-symbols-outlined">ambient_screen</i>
                                            <v-list-item-title>AI Auto Correct <span class="control-label--new">NEW</span></v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item :class="{ '--selected': false }"
                                                     @click="AIAutoCorrectV2Handler('favor-context', item)"
                                        >
                                            <v-list-item-title>Favor Context</v-list-item-title>
                                        </v-list-item>

                                        <v-list-item :class="{ '--selected': false }"
                                                     @click="AIAutoCorrectV2Handler('favor-neutrality', item)"
                                        >
                                            <v-list-item-title>Favor Neutrality</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-list-item
                                    @click="downloadLUT(item)"
                                    v-if="!isGuest"
                                    :disabled="firstSwap || !canExportLUT"
                                >
                                    <v-icon class="material-symbols-outlined">deployed_code_update</v-icon>
                                    <v-list-item-title>Download a LUT</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    @click="downloadXMP(item)"
                                    v-if="!isGuest"
                                    :disabled="firstSwap || !canExportXMP"
                                >
                                    <img src="/assets/icons/lr-icon-96x96.png" />
                                    <v-list-item-title>Download XMP Profile</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    @click="downloadCO(item)"
                                    v-if="!isGuest"
                                    :disabled="firstSwap || !canExportCO"
                                >
                                    <img src="/assets/icons/co-icon-96x96.png" />
                                    <v-list-item-title>Download Capture One Style</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    @click="compareView(item)"
                                    v-if="!isGuest"
                                    :disabled="selectedFiles.length <= 1"
                                >
                                    <i class="material-icons">photo</i>
                                    <v-list-item-title>Compare View</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'match'" @click="downloadImage(item)">
                                    <i class="material-icons">photo</i>
                                    <v-list-item-title>Save Image</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <template v-if="item.type == 'candidate' && !isGuest">
                                    <v-list-item v-if="!fileHasTag(item, 'selects', 'selected')" @click="addToSelects(item)"
                                                 :disabled="!canAddToSelects"
                                    >
                                        <i class="material-icons">stars</i>
                                        <v-list-item-title>Add to Selects</v-list-item-title>
                                        <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                    </v-list-item>

                                    <v-list-item v-else @click="removeFromSelects(item)" :disabled="!canAddToSelects">
                                        <v-icon class="material-icons">block</v-icon>
                                        <v-list-item-title>Remove from Selects</v-list-item-title>
                                        <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                    </v-list-item>
                                </template>

                                <v-list-item v-if="!isGuest" @click="copyTo(item)" :disabled="isFileUploading(item) || !canCopyAsset">
                                    <i class="material-icons">content_copy</i>
                                    <v-list-item-title>Copy {{ capitalizeFirstLetter(item.type) }} To...</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'image'" @click="saveAllAsCandidates(item)"
                                             :disabled="isFileUploading(item) || !canCreateAssets || !item.IDT || item.IDT.initial"
                                >
                                    <i class="material-symbols-outlined">difference</i>
                                    <v-list-item-title>Save as Candidate</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'image'" @click="copyCandidateFrom(item)"
                                             :disabled="isFileUploading(item) || !canCopyAsset"
                                >
                                    <i class="material-icons">file_copy</i>
                                    <v-list-item-title>Copy Candidate From...</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <template v-if="['candidate', 'match'].includes(item.type) && !isGuest">
                                    <v-list-item @click="applyGrade(item)"
                                                 :disabled="selectedFiles.length > 1 || !canUseColorGradingTools"
                                    >
                                        <i class="material-icons">perm_media</i>
                                        <v-list-item-title>Apply Grade</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item @click="applyGradeTo(item)"
                                                 :disabled="selectedFiles.length > 1 || !canUseColorGradingTools"
                                    >
                                        <i class="material-icons">perm_media</i>
                                        <v-list-item-title>Apply Grade To...</v-list-item-title>
                                    </v-list-item>
                                </template>

                                <v-list-item v-if="item.type == 'image'" @click="applyGradeFrom(item)"
                                             :disabled="!canUseColorGradingTools || !item.IDT || item.IDT.initial"
                                >
                                    <i class="material-icons">perm_media</i>
                                    <v-list-item-title>Apply Grade From...</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'image'" @click="openAIColourMatchModalV2(item)"
                                             :disabled="firstSwap || selectedFiles.length > 1 || !canUseColorGradingTools || !item.IDT || item.IDT.initial"
                                >
                                    <i class="material-icons">auto_awesome</i>
                                    <v-list-item-title>AI Colour Match To...</v-list-item-title>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'image'" @click="openColourMatchV2Modal(item)"
                                             :disabled="firstSwap || selectedFiles.length > 1 || !canUseColorGradingTools || !item.IDT || item.IDT.initial"
                                >
                                    <i class="material-icons">filter</i>
                                    <v-list-item-title>Colour Match To...</v-list-item-title>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'match'" @click="AIColourMatchV2(item)"
                                             :disabled="firstSwap || selectedFiles.length > 1 || !canUseColorGradingTools"
                                >
                                    <i class="material-icons">auto_awesome</i>
                                    <v-list-item-title>AI Colour Match</v-list-item-title>
                                </v-list-item>

                                <v-list-item v-if="item.type == 'match'" @click="colourMatch(item)"
                                             :disabled="firstSwap || selectedFiles.length > 1 || !canUseColorGradingTools"
                                >
                                    <i class="material-icons">filter</i>
                                    <v-list-item-title>Colour Match</v-list-item-title>
                                </v-list-item>

                                <template v-if="['candidate'].includes(item.type) && !isGuest">
                                    <v-list-item v-if="!fileHasTag(item, 'shared', 'shared')"
                                                 :disabled="isFileUploading(item) || !canShareAsset"
                                                 @click="shareFile(item)"
                                    >
                                        <v-icon class="material-icons">mdi-share</v-icon>
                                        <v-list-item-title>Share</v-list-item-title>
                                        <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                    </v-list-item>

									<template v-else>
										<v-list-item @click="unshareFile(item)" :disabled="!canShareAsset">
											<v-icon class="material-icons">block</v-icon>
											<v-list-item-title>Unshare</v-list-item-title>
											<i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
										</v-list-item>

										<v-menu
											v-if="item.folder_slug == 'shared'"
											max-width="170"
											offset-x
											left
											:nudge-top="25"
											open-on-click
											:content-class="'file-options-container --rating --level2'"
										>
											<template v-slot:activator="{ on, attrs }">
												<v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on" :disabled="!canShareAsset || !activeImage">
													<i class="material-icons arrow-left">arrow_left</i>
													<v-icon class="material-symbols-outlined">download</v-icon>
													<v-list-item-title>Add to Downloads <span class="control-label--new">NEW</span></v-list-item-title>
													<i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
												</v-list-item>
											</template>
											<v-list>
												<v-list-item :class="{ '--selected': item.downloadable && getExtension(item.downloadable) == 'jpg' }" @click="addToDownloadsHandler(item, 'jpg')">
													<v-list-item-title>JPG</v-list-item-title>
												</v-list-item>
												<v-list-item :class="{ '--selected': item.downloadable && getExtension(item.downloadable) == 'png' }" @click="addToDownloadsHandler(item, 'png')">
													<v-list-item-title>PNG</v-list-item-title>
												</v-list-item>
												<v-list-item :class="{ '--selected': item.downloadable && getExtension(item.downloadable) == 'tiff' }" @click="addToDownloadsHandler(item, 'tiff')">
													<v-list-item-title>TIFF</v-list-item-title>
												</v-list-item>
											</v-list>
										</v-menu>

										<v-list-item v-if="item.folder_slug == 'shared'" @click="removeFromDownloadsHandler(item)" :disabled="!item.downloadable">
											<v-icon class="material-symbols-outlined">delete</v-icon>
											<v-list-item-title>Remove Download</v-list-item-title>
										</v-list-item>
									</template>
                                </template>

                                <v-list-item v-if="!isGuest" @click="startFileRenaming(item)"
                                             :disabled="isFileUploading(item) || !canRenameAsset"
                                >
                                    <i class="material-icons">create</i>
                                    <v-list-item-title>Rename</v-list-item-title>
                                </v-list-item>

                                <v-menu
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --rating --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator"
                                                     v-bind="attrs"
                                                     v-on="on"
                                                     :disabled="!canAssignTagToAsset"
                                        >
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">star_rate</i>
                                            <v-list-item-title>Rating</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="ratingTag in tags.rating"
                                            :key="ratingTag.value"
                                            class="stars-icons-container"
                                            :class="{ '--selected': fileHasTag(item, 'rating', ratingTag.value) }"
                                            @click="addTag(item, 'rating', ratingTag.value)"
                                        >
                                            <v-list-item-title v-if="ratingTag.value == 'none'">{{
                                                    ratingTag.label
                                                }}
                                            </v-list-item-title>
                                            <i v-else v-for="index in ratingTag.value" :key="index" class="material-icons">star_rate</i>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on" :disabled="!canAssignTagToAsset">
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">fiber_manual_record</i>
                                            <v-list-item-title>Status</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="statusTag in tags.status"
                                            :key="statusTag.value"
                                            :class="{ '--selected': fileHasTag(item, 'status', statusTag.value) }"
                                            @click="addTag(item, 'status', statusTag.value)"
                                        >
                                            <i class="material-icons" :style="{ color: statusTag.styleColor }">fiber_manual_record</i>
                                            <v-list-item-title>{{ statusTag.label }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    max-width="170"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --level2'"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on" :disabled="!canAssignTagToAsset">
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">label</i>
                                            <v-list-item-title>Tag</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="colorTag in tags.color"
                                            :key="colorTag.value"
                                            :class="{ '--selected': fileHasTag(item, 'color', colorTag.value) }"
                                            @click="addTag(item, 'color', colorTag.value)"
                                        >
                                            <i class="material-icons" :style="{ color: colorTag.styleColor }">label</i>
                                            <v-list-item-title>{{ colorTag.label }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    v-if="item.type == 'image'"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --overflow --level2 --IDT'"
                                    :close-on-content-click="false"
                                    :max-height="390"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on"
                                                     :disabled="firstSwap || !canUseColorGradingTools"
                                        >
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">adjust</i>
                                            <v-list-item-title>ACES Input Transform</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="IDT in IDTList"
                                            :key="IDT.value"
                                            class="full-span"
                                            :class="{
											'--selected': fileOptions.IDT && fileOptions.IDT.value == IDT.value && !fileOptions.IDT.initial,
											'--first': IDT.label == 'No Input Transform',
											'hidden': IDT.hidden
										}"
                                            @click="onIDTODTselection(item, 'IDT', IDT)"
                                        >
                                            <v-list-item-title>{{ IDT.label }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-menu
                                    v-if="item.type == 'image'"
                                    offset-x
                                    left
                                    :nudge-top="25"
                                    open-on-click
                                    :content-class="'file-options-container --overflow --level2 --ODT'"
                                    :close-on-content-click="false"
                                    :max-height="390"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item class="sub-menu-activator" v-bind="attrs" v-on="on"
                                                     :disabled="firstSwap || !canUseColorGradingTools"
                                        >
                                            <i class="material-icons arrow-left">arrow_left</i>
                                            <i class="material-icons">adjust</i>
                                            <v-list-item-title>ACES Output Transform</v-list-item-title>
                                            <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                        </v-list-item>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="ODT in ODTList"
                                            :key="ODT.value"
                                            class="full-span"
                                            :class="{
											'--selected': fileOptions.ODT && fileOptions.ODT.value == ODT.value && !fileOptions.ODT.initial,
											'--first': ODT.label == 'No Output Transform',
                                            'hidden': ODT.hidden
										}"
                                            @click="onIDTODTselection(item, 'ODT', ODT)"
                                        >
                                            <v-list-item-title>{{ ODT.label }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>

                                <v-list-item v-if="!isGuest" @click="moveToArchive(item)"
                                             :disabled="isFileUploading(item) || !canArchiveAsset"
                                >
                                    <v-icon class="material-icons">mdi-restore</v-icon>
                                    <v-list-item-title>Archive</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <v-list-item v-if="!isGuest" @click="moveToTrash(item)"
                                             :disabled="isFileUploading(item) || !canDeleteAsset"
                                >
                                    <v-icon class="material-icons">mdi-delete</v-icon>
                                    <v-list-item-title>Delete</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>

                                <v-list-item v-if="!isGuest" @click="syncImageState(item)"
                                             :disabled="isFileUploading(item) || !canSyncImageState"
                                >
                                    <v-icon class="material-icons">mdi-sync</v-icon>
                                    <v-list-item-title>Sync Now</v-list-item-title>
                                </v-list-item>
                            </v-list>

                            <v-list v-else-if="!item.archived_at && item.trashed_at">
                                <v-list-item @click="restoreTrashedFile(item)" :disabled="!canRestoreAsset">
                                    <i class="material-icons import-images">settings_backup_restore</i>
                                    <v-list-item-title>Restore</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>
                                <v-list-item @click="deleteFile(item)" :disabled="!canDeleteAsset">
                                    <v-icon class="material-icons">mdi-delete</v-icon>
                                    <v-list-item-title>Delete Permanently</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>
                            </v-list>

                            <v-list v-else-if="item.archived_at && !item.trashed_at">
                                <v-list-item @click="restoreArchivedFile(item)" :disabled="!canRestoreAsset">
                                    <i class="material-icons import-images">settings_backup_restore</i>
                                    <v-list-item-title>Restore</v-list-item-title>
                                    <i class="material-icons" v-if="selectedFiles.length > 1">more_horiz</i>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>

                    <div class="menu-container" v-if="['bin', 'main'].includes(item.type) && !isGuest">
                        <v-menu left nudge-left="20px" :content-class="'file-options-container'">
                            <template v-slot:activator="{ on, attrs }">
                                <i class="material-icons" v-bind="attrs" :bin_id="item.id" v-on="on">more_vert</i>
                            </template>
                            <v-list>
                                <v-list-item @click="startBinRenaming(item)" :disabled="!canRenameBin">
                                    <i class="material-icons import-images">create</i>
                                    <v-list-item-title>Rename</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="deleteBin(item)" :disabled="item.type == 'bin' || !canDeleteBin">
                                    <v-icon class="material-icons">mdi-delete</v-icon>
                                    <v-list-item-title>Delete</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>

					<div class="menu-container" v-if="['trash'].includes(item.slug) && !isGuest">
						<v-menu left nudge-left="20px" :content-class="'file-options-container'">
							<template v-slot:activator="{ on, attrs }">
								<i class="material-icons" v-bind="attrs" v-on="on">more_vert</i>
							</template>
							<v-list>
								<v-list-item @click="emptyTrash(item)" :disabled="!canDeleteAsset">
									<v-icon class="material-icons">mdi-delete</v-icon>
									<v-list-item-title>Empty Trash</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>

					<template v-if="['shared'].includes(item.slug) || (['bin', 'main'].includes(item.type) && isGuest)">
						<v-icon v-if="hasDownloadableMediaItems(item)"
								v-tippy
								content="Download All"
								class="material-symbols-outlined"
								size="18"
								@click.stop="downloadAllDownloadableMedia(item)"
						>
							download
						</v-icon>
					</template>

				</template>
            </v-treeview>
        </div>
    </div>
</template>

<script>
import { cloneDeep } from "lodash";
import download from 'downloadjs';
import JSZip from "jszip";

import { mapActions, mapGetters, mapState } from "vuex";
import FilenameMixin from "@/mixins/Filename";
import CanMixin from "@/mixins/CanMixin";
import { readAndCompressImage } from "browser-image-resizer";
import ProjectTreeSearchAndFilters from "@/components/app/project-tree/ProjectTreeSearchAndFilters.vue";
import fileTags from "@/components/app/files/FileTags.vue";
import Thumbnail from "@/components/app/projects/Thumbnail.vue";
import NewCommentNotificationsCounter from "@/components/app/notifications/NewCommentNotificationsCounter.vue";
import { stripExtension, getTimestamp, stripTimestampAndExtension, getExtension } from "@/assets/js/Utilities";
import $ from 'jquery'

export default {
    name: "ProjectTree",
    mixins: [FilenameMixin, CanMixin],
    components: {
        ProjectTreeSearchAndFilters,
        fileTags,
        Thumbnail,
        NewCommentNotificationsCounter
    },

    data() {
        return {
            switchingImageInterval: false,
            firstSwap: true,
            selectedFiles: [],
            fileOptions: {
                IDT: null,
                ODT: null,
            },
            renamingBinId: null,
            renamingBinName: null,
            renamingFileKey: null,
            renamingFileName: null,
        };
    },
    computed: {
        ...mapState("Projects", ["project", "projectTree", "appliedFilters"]),
        ...mapState("App", ["IDTList", "ODTList", "blockLoading"]),
        ...mapState("Projects", {
            activeCandidate: "candidate",
            activeMatch: "match",
            activeImage: "image",
            openProjectTree: "openProjectTree",
            tags: "tags",
            search: "projectTreeSearch",
        }),
        ...mapState("UI", ["startTips"]),
        ...mapGetters("Projects", [
            "processingBinName",
            // "files",
            "bins",
            "binFolders",
            "candidates",
            "matches",
            "trashedFiles",
            "archivedFiles",
            "folderIdBySlug"
        ]),
        ...mapGetters('User', ['isGuest', 'isMemberOrLower']),
        ...mapGetters('Notifications', ['notificationsOfFile', 'newCommentNotificationsOfFile', 'newCommentNotificationsOfBin', 'newCommentNotificationsOfFolder']),
        selectedFilesTest() {
            return this.selectedFiles.map(e => e.type);
        },
        neuralToneAIModelNamesList() {
            return window._Adjustments.NeuralToneAI.modelNamesList
        },
        neuralFilmAIModelNamesList() {
            return window._Adjustments.NeuralFilmAI.modelNamesList
        }
    },
    created() {
        window.readAndCompressImage = readAndCompressImage;
        window.projectTree = this; // NOTE: refactor
        this.$root.projectTree = this;
    },
    mounted() {
        this.$root.projectTreeComponent = this.$refs.projectTree;
        this.$store.dispatch("Projects/setProjectTreeComponent", this.$refs.projectTree.updateAll);
    },
    methods: {
        ...mapActions("App", ["applyGrade"]),
        ...mapActions("Helpers", ["revokeObjectURL"]),

        nicename(name, stripTimestampOnly = false) {
            if (stripTimestampOnly) return this.stripTimestamp(name);
            else return this.stripTimestampAndExtension(name);
        },
        createBin() {
            window.UI.showModal(window.UI.modals.bin.create);
        },
        // editBin(bin) {
        // 	this.$store.dispatch("Projects/setAction", {
        // 		type: "bin",
        // 		action: "edit",
        // 		data: bin,
        // 	});
        // 	window.UI.showModal(window.UI.modals.bin.edit);
        // 	window.UI.modals.bin.edit.find("input").val(bin.name);
        // },
        deleteBin(bin) {
            this.$store.dispatch("Projects/setAction", {
                type: "bin",
                action: "delete",
                data: bin,
            });
            window.UI.showModal(window.UI.modals.bin.delete);
        },
		emptyTrash(trashFolder) {
			this.$store.dispatch("Projects/setAction", {
				type: "trash",
				action: "delete",
				data: trashFolder,
			});
			window.UI.showModal(window.UI.modals.trashFolder.empty);
		},
        startBinRenaming(bin) {
            this.$store.dispatch("Projects/setAction", {
                type: "bin",
                action: "bin-rename",
                data: bin,
            });

            this.renamingBinId = bin.id;
            this.renamingBinName = bin.name;
        },
        async renameBin(e) {
            if (e.key == "Enter" || e.keyCode == 13) {
                if (this.renamingBinName !== this.$store.state.Projects.action.data.name) {
                    await this.$store.dispatch("Projects/updateBin", this.renamingBinName);
                    await this.$store.dispatch("Projects/refreshProjectTree");
                }

                this.endBinRenaming();
            } else if (e.key == "Escape" || e.keyCode == 27) {
                this.endBinRenaming();
            }
        },
        endBinRenaming() {
            this.renamingBinId = null;
            this.$store.dispatch("Projects/setAction", {
                type: "bin",
                action: "bin-rename-end",
                data: null,
            });
        },
        startFileRenaming(file) {
            this.$store.dispatch("Projects/setAction", {
                type: "file",
                action: "file-rename",
                data: file,
            });

            this.renamingFileKey = file.key;
            this.renamingFileName = this.nicename(file.name);
        },
        async renameFile(e) {
            if (e.key == "Enter" || e.keyCode == 13) {
                this.renamingFileKey = null;

                let oldFilename = this.$store.state.Projects.action.data.name;
                let fileType = oldFilename.substring(oldFilename.lastIndexOf(".") + 1);
                let newFilename = `${this.renamingFileName}.${Date.now()}.${fileType}`;

                if (this.nicename(oldFilename) !== this.renamingFileName) {
                    await this.$store.dispatch("Projects/updateFileName", {
                        name: newFilename,
                    });

                    if (this.$store.state.Projects.action.data.type == "match") {
                        await this.$store.dispatch("UI/renameMatch", {
                            id: this.$store.state.Projects.action.data.id,
                            name: newFilename,
                            nicename: this.stripTimestampAndExtension(newFilename),
                        });

                        let matchesStoreData = await matchesStore.getItem(this.$store.state.Projects.action.data.id);
                        if (matchesStoreData)
                            matchesStore.setItem(matchesStoreData.id, {
                                ...matchesStoreData,
                                name: newFilename,
                                nicename: this.renamingFileName
                            });
                    }

                    if (this.$store.state.Projects.action.data.type == "candidate") {
                        await this.$store.dispatch("UI/renameCandidate", {
                            id: this.$store.state.Projects.action.data.id,
                            name: newFilename,
                            nicename: this.stripTimestampAndExtension(newFilename),
                        });

                        let candidateStoreData = await candidatesStore.getItem(this.$store.state.Projects.action.data.id);
                        if (candidateStoreData)
                            candidatesStore.setItem(candidateStoreData.id, {
                                ...candidateStoreData,
                                name: newFilename,
                                nicename: this.renamingFileName
                            });
                    }

                    if (this.$store.state.Projects.action.data.type == "image") {
                        let imageStoreData = await imagesStore.getItem(this.$store.state.Projects.action.data.id);
                        if (imageStoreData) imagesStore.setItem(imageStoreData.id, {
                            ...imageStoreData,
                            name: newFilename
                        });
                    }

                    await vm.$store.dispatch("Projects/refreshProjectTree", {
                        createPresignedURL: true,
                    });
                }

                this.endFileRenaming();
            } else if (e.key == "Escape" || e.keyCode == 27) {
                this.endFileRenaming();
            }
        },
        endFileRenaming() {
            this.renamingFileKey = null;
            this.$store.dispatch("Projects/setAction", {
                type: "file",
                action: "file-rename-end",
                data: null,
            });
        },

        async deleteFile(file) {
            await this.$store.dispatch("Projects/selectBin", file.bin_id);
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            if (selectedFiles.length > 500) {
                alert("You can only archive a maximum of 500 files at once.");
                return;
            }

            this.selectedFiles = [];
            vm.$store.dispatch("Loader/showProgressBar", "indeterminate");

            await this.$store.dispatch("Projects/deleteFile", {
                file_id: file.id,
                file_ids: selectedFiles.map(e => e.id),
                folder_id: file.trash_id,
            });

            await vm.$store.dispatch("Projects/refreshProjectTree");
            vm.$store.dispatch("Loader/showProgressBar", false);
        },

        getInBetweenFileIds(files, startFile, endFile) {
            let startIndex = files.map(e => e.key).indexOf(startFile.key);
            let endIndex = files.map(e => e.key).indexOf(endFile.key);
            // if (!startIndex || !endIndex) return;
            return (
                files
                    // .map(fileItem => fileItem.id)
                    .filter((fileItem, index) => {
                        if (startIndex < endIndex) {
                            // shift-clicling down the list
                            if (index >= startIndex && index <= endIndex) return true;
                        } else {
                            // shift-clicking up the list
                            if (index >= endIndex && index <= startIndex) return true;
                        }
                    })
            );
        },

        async onFileSelection($event, bin_id, file) {
            if (this.$store.state.App.blockLoading) return;
            if (this.switchingImageInterval) return;

            this.switchingImageInterval = true;
            setTimeout(() => (this.switchingImageInterval = false), 1000);

            if ($event.shiftKey && !$event.altKey && this.selectedFiles.length == 1) {
                let targetBin = this.projectTree.find(bin => bin_id == bin.id);
                let targetFolder = [];

                let startFile = this.selectedFiles[0];
                let endFile = file;

                if (file.type == "image") {
                    if (file.archived_at)
                        targetFolder = targetBin.children
                            .find(folder => folder.slug == "archive")
                            .children.find(folder => folder.name == "Media")
                            .children.filter(fileItem => fileItem.type == "image");
                    else if (file.trashed_at)
                        targetFolder = targetBin.children
                            .find(folder => folder.slug == "trash")
                            .children.find(folder => folder.name == "Media")
                            .children.filter(fileItem => fileItem.type == "image");
                    else
                        targetFolder = targetBin.children.find(folder => folder.slug == "media").children.filter(fileItem => fileItem.type == "image");
                }
                else if (file.type == "match") {
                    if (file.archived_at)
                        targetFolder = targetBin.children
                            .find(folder => folder.slug == "archive")
                            .children.find(folder => folder.name == "Matches")
                            .children.filter(fileItem => fileItem.type == "match");
                    else if (file.trashed_at)
                        targetFolder = targetBin.children
                            .find(folder => folder.slug == "trash")
                            .children.find(folder => folder.name == "Matches")
                            .children.filter(fileItem => fileItem.type == "match");
                    else
                        targetFolder = targetBin.children
                            .find(folder => folder.slug == "matches")
                            .children.filter(fileItem => fileItem.type == "match");
                }
                else if (file.type == "candidate") {
                    if (file.archived_at)
                        targetFolder = targetBin.children
                            .find(folder => folder.slug == "archive")
                            .children.find(folder => folder.name == "Candidates")
                            .children.filter(fileItem => fileItem.type == "candidate");
                    else if (file.trashed_at)
                        targetFolder = targetBin.children
                            .find(folder => folder.slug == "trash")
                            .children.find(folder => folder.name == "Candidates")
                            .children.filter(fileItem => fileItem.type == "candidate");
                    else {
						// TODO not workign properly
						// candidates in 'selects' folder
                        if (this.selectedFiles[0].folder_slug == "selects") {
                            targetFolder = targetBin.children.find(folder => folder.slug == "selects").children;
                        }

						// TODO not workign properly
						// candidates in 'shared' folder
						else if (this.selectedFiles[0].folder_slug == "shared") {
							targetFolder = targetBin.children.find(folder => folder.slug == "shared").children;
							console.log(targetFolder)
						}

                        // candidates in 'media' folder
                        else {
                            if (startFile.id > endFile.id) {
                                let startFileTemporary = startFile;
                                startFile = endFile;
                                endFile = startFileTemporary;
                            }

                            const startParentFile = startFile.parent_file;
                            const endParentFile = endFile.parent_file;

                            const startBin = this.$store.getters['Projects/binById'](startFile.bin_id);
                            const endBin = this.$store.getters['Projects/binById'](endFile.bin_id);

                            let startBinFound = false
                            let endBinFound = false

                            // getting bins in between the selection
                            const listOfSelectedBins = this.$store.getters['Projects/bins'].filter(bin => {
                                if (!startBinFound && bin.id == startBin.id) {
                                    startBinFound = true;
                                    return bin;
                                }

                                if (startBinFound && !endBinFound) {
                                    if (bin.id == endBin.id) endBinFound = true;
                                    return bin;
                                }
                            })

                            let startMediaFound = false;
                            let endMediaFound = false;
                            const listOfSelectedMedia = [];

                            listOfSelectedBins.forEach(bin => {
                                this.$store.getters['Projects/imagesByBinId'](bin.id).filter(media => {
                                    if (!startMediaFound && media.id == startParentFile.id) {
                                        startMediaFound = true;
                                        listOfSelectedMedia.push(media);
                                        return media;
                                    }

                                    if (startMediaFound && !endMediaFound) {
                                        if (media.id == endParentFile.id) endMediaFound = true;
                                        listOfSelectedMedia.push(media);
                                        return media;
                                    }
                                })
                            })

                            let startCandidateFound = false;
                            let endCandidateFound = false;
                            const listOfSelectedCandidates = [];

                            listOfSelectedMedia.forEach(media => {
                                this.$store.getters['Projects/candidates'](media.id).filter(candidate => {
                                    if (!startCandidateFound && candidate.id == startFile.id) {
                                        startCandidateFound = true;
                                        listOfSelectedCandidates.push(candidate);
                                        return candidate;
                                    }

                                    if (startCandidateFound && !endCandidateFound) {
                                        if (candidate.id == endFile.id) endCandidateFound = true;
                                        listOfSelectedCandidates.push(candidate);
                                        return media;
                                    }
                                })
                            })

                            // filtering out excluded items from filter and search results
                            this.selectedFiles = listOfSelectedCandidates.filter(candidate => !this.$refs.projectTree.excludedItems.has(candidate.key));
                            return;
                        }
                    }
                }

                if (startFile.bin_id !== endFile.bin_id) return; // clicked file from another bin
                if (startFile.type !== endFile.type) return; // clicked file with another type
                if (!!startFile.archived_at !== !!endFile.archived_at) return; // clicked item in archive folder
                if (!!startFile.trashed_at !== !!endFile.trashed_at) return; // clicked item in trash folder
                const result = this.getInBetweenFileIds(targetFolder, startFile, endFile);
                if (result) this.selectedFiles = result;
                return;
            } else if ($event.altKey && !$event.shiftKey && this.selectedFiles.length >= 1) {
                if (this.selectedFiles[0].type !== file.type) return;
                if (this.selectedFiles[0].key == file.key) return;
                if (this.selectedFiles[0].bin_id !== file.bin_id) return;
                if (this.selectedFiles[0].type == "candidate" && this.selectedFiles[0].parent_file.key !== file.parent_file.key) return;

                if (this.selectedFiles.map(e => e.key).includes(file.key) && this.selectedFiles.length > 1)
                    this.selectedFiles = this.selectedFiles.filter(({ key }) => key !== file.key);
                else this.selectedFiles.push(file);
                return;
            }

            this.selectedFiles = [file];
            await this.$store.dispatch("Projects/selectBin", bin_id);

            if (file.type == "image") {
                await this.switchImage(bin_id, file);
                if (lutify.activePreviewsSidebarContainer == 'comments') lutify.markNewCommentNotificationsAsRead();
            } else if (file.type == "match") {
                if (file.folder_slug == 'shared') {
                    this.switchImage(bin_id, file);
                    return;
                }

                // file outside shared folder
                if (!this.$store.state.Projects.image) return;
                window.UI.container.matches.find(`img[data-index="${file.id}"]`).trigger("click");
            } else if (file.type == "candidate") {
                if (file.trashed_at || file.archived_at) return;
                window.UI.appendFileLoadingSpinner(file.id);
                this.$store.dispatch("Projects/setCurrentFile", file);

                if (this.isGuest) {
                    await this.switchImageForGuests(bin_id, file);
                } else {
                    if (this.$store.state.Projects.image !== file.parent_file.id) {
                        await this.switchImage(bin_id, file.parent_file, true);
                    }

                    this.$store.dispatch('Comments/switch', file.folder_slug);
                    window.UI.container.compares.find(`.candidate-container[data-index="${file.id}"] img`).trigger("click");
                }

                // if (lutify.activePreviewsSidebarContainer == 'comments') lutify.markNewCommentNotificationsAsRead();
            }
        },

        async switchImage(bin_id, file, applyingCandidate = false) {

           // if (this.$store.state.Projects.image == file.id) return; // when clicking on same image, do nothing
            if (file.trashed_at || file.archived_at) return;
            if (this.firstSwap || this.$store.state.Projects.projectSwitch) {
                window.UI.container.mainLayoutMiddleBottomArea.removeClass("hide");
                window.$("#wrapper").css("display", "flex");
            }

            if (!applyingCandidate) window.UI.appendFileLoadingSpinner(file.id);
            window.UI.showImageLoader();

            window.lutify.removeSplitView();
            window.loader.imageName = this.nicename(file.name);
            this.$store.dispatch("Projects/setActiveCandidate", null);

            let storeData = await window.lutify.fetchImage(file);

            await window.loader.areRequiredFilesLoaded();
            if (! await window.loader.setActiveMedia(storeData.blob, storeData.fileType, file.id)) return;
            await window.loader.loadActiveMedia();

            // saving images current state, before updating select image object
            if (this.firstSwap) {
                window.lutify.initialBlocks = cloneDeep(window.lutify.getRawBlocksData());
            } else if (!this.$store.state.Projects.projectSwitch) {
                await window.lutify.storeLastState(null, null, loader.previousMedia);
            }

            if (loader.typeOfActiveMedia('raw')) {
                let imageState = await window.lutify.fetchLastState(file.id);
                if (imageState?.rawDevelopment?.develop) {
                    vm.rawDevelopment.setValues(imageState.rawDevelopment)
                } else if (imageState?.rawDevelopment?.colourSpace) {
                    // backwards compatibility
                    vm.rawDevelopment.setValues({
                        develop: true
                    })
                } else {
                    vm.rawDevelopment.resetValues();
                }
            } else {
                this.$store.dispatch('Projects/setExif', {width: null, height: null})
            }

            // updating selected image object
            this.$store.dispatch("Projects/setImage", file.id);
            if (!applyingCandidate) this.$store.dispatch("Projects/setCurrentFile", file);

            this.$store.dispatch('Comments/switch');

            // applying last state
            if (!this.isMemberOrLower) await window.lutify.applyLastState(file.id);

            if (!applyingCandidate && !vm.rawDevelopment.updating) {
                window.UI.hideImageLoader();
                window.UI.removeFileLoadingSpinner(file.id);
            }

            if (loader.typeOfActiveMedia('raw')) {
                let imageState = await window.lutify.fetchLastState(file.id);
                if (imageState?.rawDevelopment?.colourSpace) window.UI.IDT.selectByName('ACES2065-1'); // backwards compatibility
                else if (!imageState?.rawDevelopment?.develop) window.UI.IDT.selectByName('sRGB');
            }

            if (this.firstSwap || this.$store.state.Projects.projectSwitch) {
                this.firstSwap = false;

                // when switching project, and first selection
                this.$store.dispatch("Projects/setProjectSwitch", false);
            }

            if (file.type == 'image') await window.lutify.switchCandidates(file.id);

			UI.toggleStartTips();

			// if (this.startTips == 1.5) {
            //     this.$store.dispatch("UI/setStartTips", 2);
            // }

            // updating thumbnail. NOTE: needs refactoring - don't update to server if not changes
            // if (await window.updateThumbnailsStore.getItem(file.id)) this.updateFileThumbnail(file);
            // this.updateFileThumbnail(file);
        },

        async switchImageForGuests(bin_id, file) {
            if (this.$store.state.Projects.image == file.id) return; // when clicking on same image, do nothing
            if (file.trashed_at || file.archived_at) return;

            let storeData = await window.lutify.fetchCandidate(file);
            if (!storeData.image) storeData = await window.lutify.fetchCandidate(file, true);
            if (!storeData.image) {
                this.$snackbar('Previously shared assets are not compatible with this version of fylm.ai. Please ask the creator of these assets to create new ones and share them with you.', 'error');
                return;
            }

            if (this.firstSwap || this.$store.state.Projects.projectSwitch) {
                window.$("#wrapper").css("display", "flex");
            }

            window.UI.appendFileLoadingSpinner(file.id);
            window.UI.showImageLoader();

            window.lutify.removeSplitView();
            window.loader.imageName = this.nicename(file.name);

            await window.loader.areRequiredFilesLoaded();
            await window.loader.setActiveMedia(storeData.image, 'jpg');
            await window.loader.loadActiveMedia();

            this.$store.dispatch("Projects/setImage", file.id);
            this.$store.dispatch("Projects/setCurrentFile", file);
            this.$store.dispatch("Projects/setActiveCandidate", file.id);

            this.$store.dispatch('Comments/switch', file.folder_slug);

            window.UI.hideImageLoader();
            window.UI.removeFileLoadingSpinner(file.id);

            if (this.firstSwap || this.$store.state.Projects.projectSwitch) {
                this.firstSwap = false;

                // when switching project, and first selection
                this.$store.dispatch("Projects/setProjectSwitch", false);
            }

            // if (this.startTips == 1.5) {
            //     this.$store.dispatch("UI/setStartTips", 2);
            // }

            // if (this.$store.state.Projects.previousBin.id !== bin_id ||
            //     this.$store.state.Comments.currentChannel !== this.$store.state.Comments.previousChannel) {
                await this.$store.dispatch('Comments/fetch');
            // }
        },

        async openImportDialog(folder_id) {
            // if (this.startTips == 1) {
            //     document.querySelector(".import-images")._tippy.hide();
            //     this.$store.dispatch("UI/setStartTips", 1.5);
            // }

            await this.$store.dispatch("Projects/setAction", {
                type: "image",
                action: "upload",
                id: folder_id,
            });

            document.getElementById("import-media").click();
        },
        startImportProcess() {
            window.loader.uploadImages();
        },
        async moveToTrash(file) {
            await this.$store.dispatch("Projects/selectBin", file.bin_id);
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            if (selectedFiles.length > 500) {
                alert("You can only delete a maximum of 500 files at once.");
                return;
            }

            this.selectedFiles = [];
            vm.$store.dispatch("Loader/showProgressBar", "indeterminate");

            if (selectedFiles[0].type == "image") {
                if (selectedFiles.length == 1) {
                    if (!UI.selectNextImage(selectedFiles[0].id)) {
                        if (!UI.selectPreviousImage(selectedFiles[0].id)) {
                            this.$store.dispatch("Projects/switchProject");
                        }
                    }
                } else this.$store.dispatch("Projects/switchProject");
            }

            for await (const selectedFile of selectedFiles) {
                if (selectedFile.type == "image") {
                    let isUploading = await window.uploadImagesStore.getItem(selectedFile.id);
                    if (isUploading) {
                        alert("Image can't be moved while uploading to server. Try again later");
                        return;
                    }
                    await window.imagesStore.removeItem(selectedFile.id);
                } else if (selectedFile.type == "match") {
                    // check if match is being uploaded
                    let isUploading = await window.uploadMatchesStore.getItem(selectedFile.id);
                    if (isUploading) {
                        alert("Matches can't be moved while uploading to server. Try again later");
                        return;
                    }

                    window.matchesStore.removeItem(selectedFile.id);
                    this.$store.dispatch("UI/removeMatch", selectedFile.id);
                } else if (selectedFile.type == "candidate") {
                    // check if candidate is being uploaded
                    let isUploading = await window.uploadCandidatesStore.getItem(selectedFile.id);
                    if (isUploading) {
                        alert("Candidate can't be moved while uploading to server. Try again later");
                        return;
                    }

                    window.candidatesStore.removeItem(selectedFile.id);
                    // window.UI.removeCandidate(selectedFile.id);
                    this.$store.dispatch("UI/removeCandidate", selectedFile.id);
                    // window.UI.setCandidatesNumber(this.$store.getters["Projects/candidates"]().length - 1);
                    // if (!(this.$store.getters["Projects/candidates"]().length - 1)) window.UI.btn.exportCompares.addClass("hide");
                }

                window
                    .$(`#project-navigator-container .file[file_id="${selectedFile.id}"`)
                    .closest(".v-treeview-node__root")
                    .remove();
            }

            await this.$store.dispatch("Projects/moveTo", {
                file_id: selectedFiles[0].id,
                action: "Trash",
                file_ids: selectedFiles.map(e => e.id),
            });

			window.UI.hideStartTips();

            await this.$store.dispatch("Projects/refreshProjectTree");
            vm.$store.dispatch("Loader/showProgressBar", false);
        },

        async moveToArchive(file) {
            await this.$store.dispatch("Projects/selectBin", file.bin_id);
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            if (selectedFiles.length > 500) {
                alert("You can only archive a maximum of 500 files at once.");
                return;
            }

            this.selectedFiles = [];
            vm.$store.dispatch("Loader/showProgressBar", "indeterminate");

            if (selectedFiles[0].type == "image") {
                if (selectedFiles.length == 1) {
                    if (!UI.selectNextImage(selectedFiles[0].id)) {
                        if (!UI.selectPreviousImage(selectedFiles[0].id)) {
                            this.$store.dispatch("Projects/switchProject");
                        }
                    }
                } else this.$store.dispatch("Projects/switchProject");
            }

            for await (const selectedFile of selectedFiles) {
                if (selectedFile.type == "image") {
                    let isUploading = await window.uploadImagesStore.getItem(selectedFile.id);
                    if (isUploading) {
                        alert("Image can't be moved while uploading to server. Try again later");
                        return;
                    }
                    await window.imagesStore.removeItem(selectedFile.id);
                } else if (selectedFile.type == "match") {
                    // check if match is being uploaded
                    let isUploading = await window.uploadMatchesStore.getItem(selectedFile.id);
                    if (isUploading) {
                        alert("Matches can't be moved while uploading to server. Try again later");
                        return;
                    }

                    window.matchesStore.removeItem(selectedFile.id);
                    this.$store.dispatch("UI/removeMatch", selectedFile.id);
                } else if (selectedFile.type == "candidate") {
                    // check if candidate is being uploaded
                    let isUploading = await window.uploadCandidatesStore.getItem(selectedFile.id);
                    if (isUploading) {
                        alert("Candidate can't be moved while uploading to server. Try again later");
                        return;
                    }

                    window.candidatesStore.removeItem(selectedFile.id);
                    // window.UI.removeCandidate(selectedFile.id);
                    this.$store.dispatch("UI/removeCandidate", selectedFile.id);
                    // window.UI.setCandidatesNumber(this.$store.getters["Projects/candidates"]().length - 1);
                    // if (!(this.$store.getters["Projects/candidates"]().length - 1)) window.UI.btn.exportCompares.addClass("hide");
                }

                window
                    .$(`#project-navigator-container .file[file_id="${selectedFile.id}"`)
                    .closest(".v-treeview-node__root")
                    .remove();
            }

            await this.$store.dispatch("Projects/moveTo", {
                file_id: selectedFiles[0].id,
                action: "Archive",
                file_ids: selectedFiles.map(e => e.id),
            });

			window.UI.hideStartTips();

			await this.$store.dispatch("Projects/refreshProjectTree");
            vm.$store.dispatch("Loader/showProgressBar", false);
        },
        async restoreTrashedFile(file) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            if (selectedFiles.length > 500) {
                alert("You can only restore a maximum of 500 files at once.");
                return;
            }

            this.selectedFiles = [];
            vm.$store.dispatch("Loader/showProgressBar", "indeterminate");

            for (const selectedFile of selectedFiles) {
                // window.$(`#project-navigator-container .file-item[file_id="${selectedFile.id}"`).remove();
                window
                    .$(`#project-navigator-container .file[file_id="${selectedFile.id}"`)
                    .closest(".v-treeview-node__root")
                    .remove();
            }

            await this.$store.dispatch("Projects/moveTo", {
                file_id: selectedFiles[0].id,
                action: "RestoreFromTrash",
                file_ids: selectedFiles.map(e => e.id),
            });

            // await this.$store.dispatch("Projects/refreshProjectTree");
            await this.$store.dispatch("Projects/refreshProjectTree", {
                createPresignedURL: true,
            });
            vm.$store.dispatch("Loader/showProgressBar", false);

            if (selectedFiles[0].type == "match") {
                window.lutify.switchMatches();
            } else if (selectedFiles[0].type == "candidate") {
                window.lutify.switchCandidates(this.$store.state.Projects.image);
            }
        },
        async restoreArchivedFile(file) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            if (selectedFiles.length > 500) {
                alert("You can only restore a maximum of 500 files at once.");
                return;
            }

            this.selectedFiles = [];
            vm.$store.dispatch("Loader/showProgressBar", "indeterminate");

            for (const selectedFile of selectedFiles) {
                window
                    .$(`#project-navigator-container .file[file_id="${selectedFile.id}"`)
                    .closest(".v-treeview-node__root")
                    .remove();
                // window.$(`#project-navigator-container .file-item[file_id="${selectedFile.id}"`).remove();
            }

            await this.$store.dispatch("Projects/moveTo", {
                file_id: selectedFiles[0].id,
                action: "RestoreFromArchive",
                file_ids: selectedFiles.map(e => e.id),
            });

            // await this.$store.dispatch("Projects/refreshProjectTree");
            await this.$store.dispatch("Projects/refreshProjectTree", {
                createPresignedURL: true,
            });
            vm.$store.dispatch("Loader/showProgressBar", false);

            if (selectedFiles[0].type == "match") {
                window.lutify.switchMatches();
            } else if (selectedFiles[0].type == "candidate") {
                window.lutify.switchCandidates(this.$store.state.Projects.image);
            }
        },
        isTrashOrArchive(slug) {
            return slug == "trash" || slug == "archive" ? true : false;
        },

        async syncImageState(file) {
            this.$store.dispatch("Loader/showProgressBar", "indeterminate");
            await window.imagesStateStore.removeItem(file.id);
            await window.lutify.applyLastState(file.id);
            setTimeout(() => {
                this.$store.dispatch("Loader/showProgressBar", false);
            }, 750);
        },
        onOpenProjectTree(items) {
            if (!this.isGuest) {
                let currentOpenBins = this.$store.state.Projects.openProjectTree.filter(item => ['bin', 'main'].includes(item.type)).map(item => item.id);
                let newOpenBins = items.filter(item => ['bin', 'main'].includes(item.type)).map(item => item.id);

                let foundBinId = newOpenBins.find(item => !currentOpenBins.includes(item));
                if (foundBinId) items.push(this.$store.getters["Projects/folderBySlug"]('media', foundBinId));
            }

            this.$store.dispatch("Projects/openProjectTree", items);
        },
        copyTo(file) {
            if (!this.selectedFiles.map(e => e.id).includes(file.id)) {
                // when only 1 selection is present, we read from data passed to this function
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action: "clone",
                    id: file.id,
                    data: [file],
                });

                this.$store.dispatch("Projects/setCopyToProjectTree", file.type);
            } else {
                if (this.selectedFiles[0].type == "candidate" && this.selectedFiles.length > 100) {
                    alert("You can only copy a maximum of 100 files at once.");
                    return;
                }

                if (this.selectedFiles.length > 500) {
                    alert("You can only copy a maximum of 500 files at once.");
                    return;
                }

                this.$store.dispatch("Projects/setAction", {
                    type: this.selectedFiles[0].type,
                    action: "clone",
                    id: this.selectedFiles[0].id,
                    data: this.selectedFiles,
                });

                this.$store.dispatch("Projects/setCopyToProjectTree", this.selectedFiles[0].type);
            }

            window.UI.showModal(window.UI.modals.files.copyTo);
        },

        async saveAllAsCandidates(file) {
            this.$store.dispatch("Loader/showProgressBar", "indeterminate");
            this.$store.commit('Projects/RESET_SAVE_ALL_AS_CANDIDATES_UNPROCESSED_LIST');
            this.$store.commit('Projects/SET_SAVE_ALL_AS_CANDIDATES_PROCESSING_FILE', '');

            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            UI.showModal(UI.modals.saveAllAsCandidates.processing.el)
            await window.lutify.refreshLastStates(selectedFiles);

            let lastStatesFromStore = await window.imagesStateStore.getItems(selectedFiles.map(e => e.id.toString()))

            // picking a new candidate name and checking against existing candidate names
            let newCandidateName = 1;
            let existingCandidatesList = this.$store.getters['Projects/candidates'](file.id);
            existingCandidatesList.every(candidate => {
                 if (existingCandidatesList.map(c => stripTimestampAndExtension(c.name).split('v')[1]).includes(newCandidateName.toString())) {
                     return newCandidateName++;
                 }
            })

            newCandidateName = 'v' + newCandidateName;

            for await (const selectedFile of selectedFiles) {
                let imageState = _.cloneDeep(lastStatesFromStore[selectedFile.id]);
                let isIDTInitial = imageState.blocks.find(block => block.name == "IDT").values.initial;

                if (isIDTInitial) {
                    this.$store.commit('Projects/ADD_SAVE_ALL_AS_CANDIDATES_UNPROCESSED_FILE' +
                        '', selectedFile);
                    continue;
                }

                this.$store.commit('Projects/SET_SAVE_ALL_AS_CANDIDATES_PROCESSING_FILE', this.stripTimestamp(selectedFile.name));

                await this.switchImage(selectedFile.bin_id, selectedFile);
                await window.lutify.addCandidate(newCandidateName, false);
            }

            this.$store.dispatch("Loader/showProgressBar", false);
            this.$store.commit('Projects/SET_SAVE_ALL_AS_CANDIDATES_PROCESSING_FILE', '');
            UI.hideModal()

            if (this.$store.state.Projects.saveAllAsCandidatesUnprocessedList.length > 0) {
                UI.showModal(UI.modals.saveAllAsCandidates.unprocessedList.el);
            }
        },

        copyCandidateFrom(file) {
            if (!this.selectedFiles.map(e => e.id).includes(file.id)) {
                // when only 1 selection is present, we read from data passed to this function
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action: "copyCandidateFrom",
                    id: file.id,
                    data: [file],
                });

                this.$store.dispatch("Projects/setCopyCandidateFromProjectTree");
            } else {
                if (this.selectedFiles.length > 100) {
                    alert("You can only copy a maximum of 100 files at once.");
                    return;
                }

                this.$store.dispatch("Projects/setAction", {
                    type: this.selectedFiles[0].type,
                    action: "copyCandidateFrom",
                    id: this.selectedFiles[0].id,
                    data: this.selectedFiles,
                });

                this.$store.dispatch("Projects/setCopyCandidateFromProjectTree");
            }

            window.UI.showModal(window.UI.modals.files.copyCandidateFrom);
        },

        applyGradeTo(file) {
            this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action: "applyGradeTo",
                id: file.id,
                data: file,
            });

            this.$store.dispatch("Projects/setApplyGradeToProjectTree");

            window.UI.showModal(window.UI.modals.grade.applyTo);
        },

        applyGradeFrom(file) {
            if (!this.selectedFiles.map(e => e.id).includes(file.id)) {
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action: "applyGradeFrom",
                    id: file.id,
                    data: [file],
                });
            } else {
                this.$store.dispatch("Projects/setAction", {
                    type: this.selectedFiles[0].type,
                    action: "applyGradeFrom",
                    id: this.selectedFiles[0].id,
                    data: this.selectedFiles,
                });
            }

            this.$store.dispatch("Projects/setApplyGradeFromCandidateProjectTree");
            this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

            window.UI.showModal(window.UI.modals.grade.applyFrom);

            // v-tab active fix
            document.querySelectorAll("#apply-grade-from-modal .v-tab")[1].click();
            setTimeout(() => document.querySelectorAll("#apply-grade-from-modal .v-tab")[0].click(), 10);
        },

        async openAIColourExtractModal(file, action = "AIColourExtractFrom") {
            if (await _Adjustments.AIColourExtract.isModelDownloaded()) {
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action,
                    id: file.id,
                    data: {
                        file,
                    },
                });

                this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

                window.UI.showModal(window.UI.modals.AIColourExtract.el);
            } else {
                _Adjustments.AIColourExtract.loadModel();
            }
        },

        // NOTE: can be deleted?
        async openAIColourExtractV2Modal(file, action = "AIColourExtractFrom") {
            if (await _Adjustments.AIColourExtractV2.isModelDownloaded()) {
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action,
                    id: file.id,
                    data: {
                        file,
                    },
                });

                this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

                window.UI.showModal(window.UI.modals.AIColourExtractV2.el);
            } else {
                _Adjustments.AIColourExtractV2.loadModel();
            }
        },

        async openAIColourMatchModal(file, action = "AIColourMatchFrom") {
            if (await _Adjustments.AIColourMatch.isModelDownloaded()) {
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action,
                    id: file.id,
                    data: {
                        file,
                    },
                });

                this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

                window.UI.showModal(window.UI.modals.AIColourMatch.el);
            } else {
                _Adjustments.AIColourMatch.loadModel();
            }
        },

        async openAIColourMatchModalV2(file, action = "AIColourMatchFrom") {
            if (await _Adjustments.AIColourMatchV2.isModelDownloaded()) {
                this.$store.dispatch("Projects/setAction", {
                    type: file.type,
                    action,
                    id: file.id,
                    data: {
                        file,
                    },
                });

                this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

                window.UI.showModal(window.UI.modals.AIColourMatchV2.el);
            } else {
                _Adjustments.AIColourMatch.loadModel();
            }
        },

        async AIColourMatchV2(match) {
            if (! await _Adjustments.AIColourMatchV2.isModelDownloaded()) {
                _Adjustments.AIColourMatch.loadModel();
                return;
            }

            this.$store.dispatch("Loader/showProgressBar", "indeterminate");

            await window._Adjustments.AIColourMatchV2.addBlock();
            await window._Adjustments.AIColourMatchV2.setSampleFile(match);

            if (lutify.activeSidebarContainer !== "blocks") {
                this.$store.dispatch('UI/setLeftColumn', 'appliedBlocks')
                UI.scrollToBlock(lutify.activeBlock);
            }

            this.$store.dispatch("Loader/showProgressBar", false);
        },

        async AIColourExtract(match) {
            if (! await _Adjustments.AIColourExtractV2.isModelDownloaded()) {
                _Adjustments.AIColourExtractV2.loadModel();
                return;
            }

            this.$store.dispatch("Loader/showProgressBar", "indeterminate");

            await window._Adjustments.AIColourExtractV2.addBlock();
            await window._Adjustments.AIColourExtractV2.setSampleFile(match);

            if (lutify.activeSidebarContainer !== "blocks") {
                this.$store.dispatch('UI/setLeftColumn', 'appliedBlocks')
                UI.scrollToBlock(lutify.activeBlock);
            }

            this.$store.dispatch("Loader/showProgressBar", false);
        },

        openColourMatchModal(file, action = "colourMatchTo") {
            this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action,
                id: file.id,
                data: {
                    file,
                },
            });
            this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

            window.UI.showModal(window.UI.modals.colourMatch.el);
        },

        openColourMatchV2Modal(file, action = "colourMatchTo") {
            this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action,
                id: file.id,
                data: {
                    file,
                },
            });
            this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

            window.UI.showModal(window.UI.modals.colourMatchV2.el);
        },

        openAdvancedColourMatchV2Modal(file, action = "colourMatchTo") {
            this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action,
                id: file.id,
                data: {
                    file,
                },
            });
            this.$store.dispatch("Projects/setApplyGradeFromMatchProjectTree");

            window.UI.showModal(window.UI.modals.advancedColourMatchV2.el);
        },

        async colourMatch(match) {
            this.$store.dispatch("Loader/showProgressBar", "indeterminate");

            await window._Adjustments.ColourMatchV2.addBlock();
            UI.toggleStartTips()
            await window._Adjustments.ColourMatchV2.setSampleFile(match);
            await window._Adjustments.ColourMatchV2.apply();

            if (lutify.activeSidebarContainer !== "blocks") {
                this.$store.dispatch('UI/setLeftColumn', 'appliedBlocks')
                UI.scrollToBlock(lutify.activeBlock);
            }

            this.$store.dispatch("Loader/showProgressBar", false);
        },

        isFileUploading(file) {
            if (file.status !== "uploaded") return true;
            else return false;
        },
        async addTag(file, tag_name, tag_value) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            if (tag_name == "rating") {
                let sameTagOnAll = selectedFiles
                    .map(e => {
                        if (e.tags.find(tag => tag.name == tag_name)) return e.tags.find(tag => tag.name == tag_name).pivot.value;
                    })
                    .every(value => tag_value == value);
                if (sameTagOnAll) {
                    await this.$store.dispatch("Projects/removeTag", {
                        file_ids: selectedFiles.map(e => e.id),
                        tag_name,
                        tag_value,
                    });

                    // NOTE: can be implemented in back-end to auto switch to none
                    await this.$store.dispatch("Projects/addTag", {
                        file_ids: selectedFiles.map(e => e.id),
                        tag_name,
                        tag_value: "none",
                    });

                    await this.$store.dispatch("Projects/refreshProjectTree");
                    // this.refreshSelectedFiles();
                    return;
                }
            }

            if (tag_name == "color") {
                let sameTagOnAll = selectedFiles
                    .map(e => e.tags.filter(tag => tag.name == "color").map(tag => tag.pivot.value))
                    .every(tag => tag.includes(tag_value));

                if (sameTagOnAll) {
                    await this.$store.dispatch("Projects/removeTag", {
                        file_ids: selectedFiles.map(e => e.id),
                        tag_name,
                        tag_value,
                    });

                    await this.$store.dispatch("Projects/refreshProjectTree");
                    // this.refreshSelectedFiles();
                    return;
                }
            }

            await this.$store.dispatch("Projects/addTag", {
                file_ids: selectedFiles.map(e => e.id),
                tag_name,
                tag_value,
            });

            await this.$store.dispatch("Projects/refreshProjectTree");
            // this.refreshSelectedFiles();
        },
        async removeTag(file, tag_name) {
            await this.$store.dispatch("Projects/removeTag", {
                file_id: file.id,
                tag_name,
            });
            this.$store.dispatch("Projects/refreshProjectTree");
        },
        // refreshSelectedFiles() {
        // let refreshedSelectedFiles = [];
        // this.selectedFiles
        // 	.map(e => e.id)
        // 	.forEach(file_id => {
        // 		refreshedSelectedFiles.push(vm.$store.getters["Projects/imageById"](file_id));
        // 	});
        // this.selectedFiles = refreshedSelectedFiles;
        // },
        fileHasTag(file, tagName, value) {
            if (file.tags) return file.tags.find(tag => tag.name == tagName && tag.pivot.value == value);
            else return false;
        },
        fileHasColorTags(file) {
        },
        getTagValues(file, tagName) {
            let tags = file.tags.filter(tag => tag.name == tagName);
            if (tags.length > 0) return tags.map(e => e.pivot.value);
            else return [];
        },
        filter(item, searchText) {
            if (searchText !== "*" && !item.name.toLowerCase().includes(searchText.toLowerCase())) return false;
            if (this.appliedFilters.length == 0) return true;

            let passedFilters = [];
            this.appliedFilters.forEach(filter => {
                // filter by tags
                if (["color", "rating", "status"].includes(filter.filter)) {
                    if (!item.tags || !item.tags.length) passedFilters.push(false);
                    else {
                        let tagValues = this.getTagValues(item, filter.filter);
                        let operationResults = [];
                        tagValues.forEach(tagValue => {
                            // this.filterOpration(filter.value, filter.operator, tagValue) ? passedFilters.push(true) : passedFilters.push(false);
                            this.filterOpration(filter.value, filter.operator, tagValue) ? operationResults.push(true) : operationResults.push(false);
                        });
                        operationResults.some(result => result == true) ? passedFilters.push(true) : passedFilters.push(false);
                    }
                }

                // filter by data
                else {
                    if (["image", "candidate", "match"].includes(item.type)) {
                        let itemValue = item[filter.filter];
                        if (filter.filter == "created_at") itemValue = itemValue.substr(0, 10);
                        this.filterOpration(filter.value, filter.operator, itemValue) ? passedFilters.push(true) : passedFilters.push(false);
                    } else passedFilters.push(false);
                }
            });

            if (passedFilters.includes(false)) return false;
            else return true;
        },
        filterOpration(filterValue, operator, value) {
            switch (operator) {
                case "==":
                    return value == filterValue;
                case "!==":
                    return value !== filterValue;
                case ">":
                    return value > filterValue;
                case "<":
                    return value < filterValue;
                case ">=":
                    return value >= filterValue;
                case "<=":
                    return value <= filterValue;
            }
        },
        async onFileOptionsOpen(file) {
            if (this.firstSwap) return;

            if (file.type !== "image") return;

            let imageState = await window.lutify.fetchLastState(file.id);
            this.fileOptions.IDT = imageState.blocks.find(block => block.name == "IDT").values;
            this.fileOptions.ODT = imageState.blocks.find(block => block.name == "ODT").values;
        },
        async onIDTODTselection(file, selectionType, values) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            await window.lutify.refreshLastStates(selectedFiles);
            let updateRemoteLastStates = [];
            let updateStoreLastStates = [];
            let timestamp = Date.now();
            let lastStatesFromStore = await window.imagesStateStore.getItems(selectedFiles.map(e => e.id.toString()));

            for await (const selectedFile of selectedFiles) {
                let imageState = _.cloneDeep(lastStatesFromStore[selectedFile.id]);
                let isIDTInitial = imageState.blocks.find(block => block.name == "IDT").values.initial;
                let ODT = null;

                // IDT-ODT combinations
                if (selectionType == "IDT" && isIDTInitial) {
                    let ODTname = window.lutify.getODTtitleFromCombination(null, values.value);
                    ODT = window.lutify.getODTbyName(ODTname);
                }

                imageState.blocks.forEach(block => {
                    if (block.name == selectionType) {
                        block.values = values;
                        if (selectionType == "IDT") block.gamutCompress = true;
                    }
                    if (ODT && block.name == "ODT") block.values = ODT;
                });

                let lastStateData = {
                    timestamp,
                    blocks: imageState.blocks,
                };

                // update UI
                if (selectionType == "IDT") this.fileOptions.IDT = values;
                if (selectionType == "ODT" || ODT) this.fileOptions.ODT = ODT ? ODT : values;

                updateRemoteLastStates.push({
                    image_id: selectedFile.id,
                    lastStateData,
                });

                updateStoreLastStates.push({
                    key: selectedFile.id.toString(),
                    value: lastStateData,
                });
            }

            await window.imagesStateStore.setItems(updateStoreLastStates);
            await this.$store.dispatch("Projects/refreshProjectTree");

            let activeImage = this.$store.state.Projects.image;
            if (selectedFiles.map(e => e.id).includes(activeImage)) {
                await window.lutify.applyLastState(activeImage);
                await this.updateFileThumbnail(
                    selectedFiles.find(file => file.id == activeImage),
                    await window.lutify.fetchLastState(activeImage)
                );
                this.$store.dispatch("Projects/refreshProjectTree");
            }

            window.lutify.postLastStates(updateRemoteLastStates);
        },

        async updateFileThumbnail(selectedFile, imageState = null) {
            let storeData = await window.lutify.fetchImage(selectedFile);
            let imageURL = URL.createObjectURL(storeData.blob);
            let image = await window.loader.getImageObject(imageURL);

            if (!imageState) imageState = _.cloneDeep(await window.lutify.fetchLastState(selectedFile.id));
            let imageStateIDT = imageState.blocks.find(block => block.name == "IDT").values;
            let imageStateODT = imageState.blocks.find(block => block.name == "ODT").values;

            await window.gpu.renderThumbnailImageOffCanvas(storeData.blob, image.width, image.height, imageStateIDT, imageStateODT);
            window.lutify.updateImageThumbnail(selectedFile.id, gpu.getThumbnailsCanvasSrc());
        },

        onSelectingFiles(files) {
            // fix for vutify treeview selecting unwanted items after refresh
            this.selectedFiles = this.selectedFiles.filter(selectedFile => selectedFile.type == this.selectedFiles[0].type);
        },

        async shareFile(file) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            let file_ids = selectedFiles.map(e => e.id);

            await this.$store.dispatch("Projects/addTag", {
                file_ids,
                tag_name: "shared",
                tag_value: "shared",
            });

            window.UI.message.activate(`Item added to Shared folder`);

            await this.$store.dispatch("Projects/refreshProjectTree");
        },

        async unshareFile(file) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            await this.$store.dispatch("Projects/removeTag", {
                file_ids: selectedFiles.map(e => e.id),
                tag_name: "shared",
                tag_value: "shared",
            });

            window.UI.message.activate(`Item removed from Shared folder`);

            await this.$store.dispatch("Projects/refreshProjectTree");
        },

        async downloadLUT(file) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            // let selectedFiles = this.selectedFiles;
            // if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            let assetsStoreData = [];
            // for await (const file of selectedFiles) {
            if (file.type == "image") {
                const image = await window.lutify.fetchImage(file);
                await window.lutify.storeLastState();
                const { blocks } = await window.lutify.fetchLastState(file.id);
                image.blocks = blocks;
                image.nicename = stripExtension(image.name);
                assetsStoreData.push(image);
            }
            else if (file.type == "candidate") assetsStoreData.push(await window.lutify.fetchCandidate(file));
            else if (file.type == "match") assetsStoreData.push(await window.lutify.fetchMatch(file));
            // }

            await this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action: "export-lut-from-asset",
                data: assetsStoreData,
            });

            this.$store.dispatch('UI/setRightColumn', 'download-lut')
        },

        async downloadXMP(file) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            // let selectedFiles = this.selectedFiles;
            // if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            let assetsStoreData = [];
            // for await (const file of selectedFiles) {
            if (file.type == "image") {
                const image = await window.lutify.fetchImage(file);
                await window.lutify.storeLastState();
                const {blocks} = await window.lutify.fetchLastState(file.id);
                image.blocks = blocks;
                image.nicename = stripExtension(image.name);
                assetsStoreData.push(image);
            }
            else if (file.type == "candidate") assetsStoreData.push(await window.lutify.fetchCandidate(file));
            else if (file.type == "match") assetsStoreData.push(await window.lutify.fetchMatch(file));
            // }

            await this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action: "export-xmp-from-asset",
                data: assetsStoreData,
            });

            this.$store.dispatch('UI/setRightColumn', 'download-xmp');
        },

        async downloadCO(file) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let assetsStoreData = [];
            // for await (const file of selectedFiles) {
            if (file.type == "image") {
                const image = await window.lutify.fetchImage(file);
                await window.lutify.storeLastState();
                const {blocks} = await window.lutify.fetchLastState(file.id);
                image.blocks = blocks;
                image.nicename = stripExtension(image.name);
                assetsStoreData.push(image);
            }
            else if (file.type == "candidate") assetsStoreData.push(await window.lutify.fetchCandidate(file));
            else if (file.type == "match") assetsStoreData.push(await window.lutify.fetchMatch(file));
            // }

            await this.$store.dispatch("Projects/setAction", {
                type: file.type,
                action: "export-co-from-asset",
                data: assetsStoreData,
            });

            this.$store.dispatch('UI/setRightColumn', 'download-co');
        },

        async addToSelects(file) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            await this.$store.dispatch("Projects/addTag", {
                file_ids: selectedFiles.map(e => e.id),
                tag_name: "selects",
                tag_value: "selected",
            });

            await this.$store.dispatch("Projects/refreshProjectTree");
        },

        async removeFromSelects(file) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            await this.$store.dispatch("Projects/removeTag", {
                file_ids: selectedFiles.map(e => e.id),
                tag_name: "selects",
                tag_value: "selected",
            });

            await this.$store.dispatch("Projects/refreshProjectTree");
        },

        async compareView(file) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            this.$store.commit('UI/RESET_COMPARE_VIEW');
            UI.showModal(UI.modals.compareView.el);

            for await (const selectedFile of selectedFiles) {
                this.$store.commit('UI/ADD_LOADING_ASSET_TO_COMPARE_VIEW');

                let storeData;
                if (selectedFile.type == "candidate") {
                    storeData = await lutify.fetchCandidate(selectedFile);

                    if (!storeData.image) storeData.image = await lutify.fetchCandidateImage(selectedFile)
                } else if (selectedFile.type == "match") {
                    storeData = await lutify.fetchMatch(selectedFile);
                    storeData.image = storeData.blob;
                } else if (selectedFile.type == "image") {
                    await this.$root.projectTree.switchImage(selectedFile.bin_id, selectedFile);
                    let img = gpu.composer.renderer.domElement.toDataURL("image/jpeg", 1);
                    let file = loader.dataURLtoFile(img, 'media');
                    storeData = await window.lutify.fetchImage(selectedFile);
                    storeData.image = file;
                }

                storeData.tags = selectedFile.tags;
                storeData.loaded = true;
                storeData.assetObject = selectedFile;

                this.$store.commit('UI/ADD_ASSET_TO_COMPARE_VIEW', storeData);
            }
        },

        async downloadImage(file) {
            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            if (selectedFiles.length == 1) {
                let matchStoreData = await window.lutify.fetchMatch(selectedFiles[0]);
                download(matchStoreData.blob, `${matchStoreData.nicename}.jpg`, "image/jpeg");
            } else {
                let zip = new JSZip();

                for await (const selectedFile of selectedFiles) {
                    let matchStoreData = await window.lutify.fetchMatch(selectedFile);
                    zip.file(matchStoreData.name, matchStoreData.blob);
                }

                zip.generateAsync({ type: "blob" }).then(blob => download(blob, `Images-${Date.now()}.zip`));
            }
        },

        async neuralToneAIHandler(modelName, file) {
            // TODO do we need this line?
            this.$store.dispatch('UI/setRightColumn', 'project'); // workaround to hide the parent menu

            this.$store.dispatch("Loader/showProgressBar", "indeterminate");
            this.$store.commit('UI/SET_NEURAL_TONE_AI_PROCESSING_STEP', 1)
            this.$store.commit('Projects/RESET_NEURAL_TONE_AI_UNPROCESSED_LIST');
            this.$store.commit('Projects/SET_NEURAL_TONE_AI_PROCESSING_FILE', '');

            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            UI.showModal(UI.modals.neuralToneAI.processing.el)
            await window.lutify.refreshLastStates(selectedFiles);

            let lastStatesFromStore = await window.imagesStateStore.getItems(selectedFiles.map(e => e.id.toString()))

            for await (const selectedFile of selectedFiles) {
                let imageState = _.cloneDeep(lastStatesFromStore[selectedFile.id]);
                let isIDTInitial = imageState.blocks.find(block => block.name == "IDT").values.initial;

                if (isIDTInitial) {
                    this.$store.commit('Projects/ADD_NEURAL_TONE_AI_UNPROCESSED_FILE', selectedFile);
                    continue;
                }

                await this.switchImage(selectedFile.bin_id, selectedFile);

                this.$store.commit('Projects/SET_NEURAL_TONE_AI_PROCESSING_FILE', this.stripTimestamp(selectedFile.name));

                await window._Adjustments.NeuralToneAI.addBlock(undefined, {
                    initial: true,
                    multiProcess: true,
                    filesToProcess: selectedFiles
                });

				this.$root.$refs.addControlButtonsContainer._tippy.hide();

				let modelIndex = window._Adjustments.NeuralToneAI.modelNamesList.indexOf(modelName);
                let $preview = $(`#previews-list-container .list-item[data-category="NeuralToneAI"][data-preset-id="${modelIndex}"]`)

                lutPreviewer.onColourMatchPreviewSelection($preview)

                let preset = luts['LUTcontrols']['NeuralToneAI'][modelIndex]
                lutify.getActiveBlock().presetId = modelIndex.toString()
                await window._Adjustments.NeuralToneAI.onPresetSelection(preset)
            }

            this.$store.dispatch("Loader/showProgressBar", false);
            this.$store.commit('Projects/SET_NEURAL_TONE_AI_PROCESSING_FILE', '');
            UI.hideModal()

            if (this.$store.state.Projects.neuralToneAIUnprocessedList.length > 0) {
                UI.showModal(UI.modals.neuralToneAI.unprocessedList.el);
            }
        },

        async neuralFilmAIHandler(modelName, file) {
            // INFO: this is a copy from neuralToneAIHandler()
            // TODO do we need this line?
            this.$store.dispatch('UI/setRightColumn', 'project'); // workaround to hide the parent menu

            this.$store.dispatch("Loader/showProgressBar", "indeterminate");
            this.$store.commit('UI/SET_NEURAL_FILM_AI_PROCESSING_STEP', 1)
            this.$store.commit('Projects/RESET_NEURAL_FILM_AI_UNPROCESSED_LIST');
            this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', '');

            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            UI.showModal(UI.modals.neuralFilmAI.processing.el)
            await window.lutify.refreshLastStates(selectedFiles);

            let lastStatesFromStore = await window.imagesStateStore.getItems(selectedFiles.map(e => e.id.toString()))

            for await (const selectedFile of selectedFiles) {
                let imageState = _.cloneDeep(lastStatesFromStore[selectedFile.id]);
                let isIDTInitial = imageState.blocks.find(block => block.name == "IDT").values.initial;

                if (isIDTInitial) {
                    this.$store.commit('Projects/ADD_NEURAL_FILM_AI_UNPROCESSED_FILE', selectedFile);
                    continue;
                }

                await this.switchImage(selectedFile.bin_id, selectedFile);

                this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', this.stripTimestamp(selectedFile.name));

                await window._Adjustments.NeuralFilmAI.addBlock(undefined, {
                    initial: true,
                    multiProcess: true,
                    filesToProcess: selectedFiles
                });

				this.$root.$refs.addControlButtonsContainer._tippy.hide();

				let modelIndex = window._Adjustments.NeuralFilmAI.modelNamesList.indexOf(modelName);
                let $preview = $(`#previews-list-container .list-item[data-category="NeuralFilmAI"][data-preset-id="${modelIndex}"]`)

                lutPreviewer.onColourMatchPreviewSelection($preview)

                let preset = luts['LUTcontrols']['NeuralFilmAI'][modelIndex]
                lutify.getActiveBlock().presetId = modelIndex.toString()
                await window._Adjustments.NeuralFilmAI.onPresetSelection(preset)
            }

            this.$store.dispatch("Loader/showProgressBar", false);
            this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', '');
            UI.hideModal()

            if (this.$store.state.Projects.neuralFilmAIUnprocessedList.length > 0) {
                UI.showModal(UI.modals.neuralFilmAI.unprocessedList.el);
            }
        },

        async AIAutoCorrectV2Handler(model, file) {
            // INFO: this is a copy from neuralToneAIHandler()
            // TODO do we need this line?
            this.$store.dispatch('UI/setRightColumn', 'project'); // workaround to hide the parent menu

            this.$store.dispatch("Loader/showProgressBar", "indeterminate");
            this.$store.commit('UI/SET_NEURAL_FILM_AI_PROCESSING_STEP', 1)
            this.$store.commit('UI/SET_NEURAL_FILM_AI_PROCESSING_MODAL_TITLE', 'AI Auto Correct')
            this.$store.commit('Projects/RESET_NEURAL_FILM_AI_UNPROCESSED_LIST');
            this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', '');

            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

            UI.showModal(UI.modals.neuralFilmAI.processing.el)
            await window.lutify.refreshLastStates(selectedFiles);

            let lastStatesFromStore = await window.imagesStateStore.getItems(selectedFiles.map(e => e.id.toString()))

            for await (const selectedFile of selectedFiles) {
                let imageState = _.cloneDeep(lastStatesFromStore[selectedFile.id]);
                let isIDTInitial = imageState.blocks.find(block => block.name == "IDT").values.initial;

                if (isIDTInitial) {
                    this.$store.commit('Projects/ADD_NEURAL_FILM_AI_UNPROCESSED_FILE', selectedFile);
                    continue;
                }

                await this.switchImage(selectedFile.bin_id, selectedFile);

                this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', this.stripTimestamp(selectedFile.name));

                await window._Adjustments.AIAutoCorrectV2.addBlock(undefined, {
                    initial: true,
                    multiProcess: true,
                    filesToProcess: selectedFiles,
                    model
                });
            }

            this.$store.dispatch("Loader/showProgressBar", false);
            this.$store.commit('Projects/SET_NEURAL_FILM_AI_PROCESSING_FILE', '');
            this.$store.commit('UI/SET_NEURAL_FILM_AI_PROCESSING_MODAL_TITLE', '')
            UI.hideModal()

            if (this.$store.state.Projects.neuralFilmAIUnprocessedList.length > 0) {
                UI.showModal(UI.modals.neuralFilmAI.unprocessedList.el);
            }
        },

		getExtension(downloadableMedia) {
			return getExtension(downloadableMedia.name)
		},

		getDownloadableMediaAsLabel(downloadableMedia) {
			return `Download as ${getExtension(downloadableMedia.name).toUpperCase()}`
		},

		async downloadDownloadableMedia(downloadableMedia) {
			this.$store.dispatch('UI/setRightColumn', 'project'); // workaround to hide the parent menu
			this.$store.dispatch("Loader/showProgressBar", "indeterminate");

			const a = document.createElement('a')
			a.href = downloadableMedia.presign_url
			// a.download = url.split('/').pop()
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)

			// const downloadableMediaBlob = await this.$store.dispatch('Files/fetchFromS3', {
			// 	url: downloadableMedia.presign_url,
			// 	config: {
			// 		responseType: "blob",
			// 	}
			// });
			//
			// const downloadableMediaFile = new File([downloadableMediaBlob], downloadableMedia.name)
			// const downloadableMediaFileExtension = getExtension(downloadableMedia.name);
			// let downloadableMediaFileMimeType = 'image/jpeg';
			//
			// if (downloadableMediaFileExtension == 'png') downloadableMediaFileMimeType = 'image/png';
			// if (downloadableMediaFileExtension == 'tiff') downloadableMediaFileMimeType = 'image/tiff';

			// download(downloadableMediaFile, downloadableMediaFile.name, downloadableMediaFileMimeType);

			this.$store.dispatch("Loader/showProgressBar", false);
		},

		hasDownloadableMediaItems(folder) {
			return folder.children.filter(file => file.downloadable).length > 0 ? true : false
		},

		async downloadAllDownloadableMedia(folder) {
			this.$store.dispatch('UI/setRightColumn', 'project'); // workaround to hide the parent menu
			this.$store.dispatch("Loader/showProgressBar", "indeterminate");
			const zip = new JSZip();

			for await (const file of folder.children) {
				if (!file.downloadable) continue;

				const downloadableMediaBlob = await this.$store.dispatch('Files/fetchFromS3', {
					url: file.downloadable.presign_url,
					config: {
						responseType: "blob",
					}
				});

				const downloadableMediaFileExtension = getExtension(file.downloadable.name);
				let downloadableMediaFileMimeType = 'image/jpeg';

				if (downloadableMediaFileExtension == 'png') downloadableMediaFileMimeType = 'image/png';
				if (downloadableMediaFileExtension == 'tiff') downloadableMediaFileMimeType = 'image/tiff';

				const downloadableMediaFile = new File([downloadableMediaBlob], file.downloadable.name, { type: downloadableMediaFileMimeType })
				zip.file(downloadableMediaFile.name, downloadableMediaFile);
			}

			zip.generateAsync({ type: "blob" }).then(blob => download(blob, `Downloads-${Date.now()}.zip`));
			this.$store.dispatch("Loader/showProgressBar", false);
		},

		async addToDownloadsHandler(file, type) {
            this.$store.dispatch('UI/setRightColumn', 'project'); // workaround to hide the parent menu
            this.$store.dispatch("Loader/showProgressBar", "indeterminate");

            let selectedFiles = this.selectedFiles;
            if (selectedFiles.length == 1 || !selectedFiles.map(e => e.id).includes(file.id)) selectedFiles = [file];

			const uploadUrlResponses = [];
			const uploadPayloadsList = [];
			const uploadPromises = [];

            for await (const selectedFile of selectedFiles) {
				const candidateName = stripTimestampAndExtension(selectedFile.name);
				const timestamp = Date.now();

				// c4.downloadable.3434234.jpg
				// c4.downloadable.thumbnail.3434234.jpg
				const downloadableMediaName = `${candidateName}.downloadable.${timestamp}`;
				// const downloadableMediaThumbnailName = `${candidateName}.downloadable.thumbnail.${timestamp}`;

				const imageDataUrl = await lutify.saveMainImage(type, downloadableMediaName, {jpgQuality: 1}, false);

				const downloadableMediaFile = loader.dataURLtoFile(imageDataUrl, `${downloadableMediaName}.${type}`);
				// let sourceFileForGeneratingThumbnail = downloadableMediaFile;

				// if (type == 'tiff') {
				// 	const imageDataUrlAsJpg = await lutify.saveMainImage('jpg', downloadableMediaName, {jpgQuality: 1}, false);
				// 	sourceFileForGeneratingThumbnail = loader.dataURLtoFile(imageDataUrlAsJpg, `${downloadableMediaName}.jpg`);
				// }
				//
				// let downloadableMediaThumbnailFile = await readAndCompressImage(sourceFileForGeneratingThumbnail, {
				// 	quality: lutify.imageThumbnail.quality,
				// 	maxWidth: lutify.imageThumbnail.maxWidth,
				// 	maxHeight: lutify.imageThumbnail.maxHeight,
				// });

				// downloadableMediaThumbnailFile = new File([downloadableMediaThumbnailFile], `${downloadableMediaThumbnailName}.jpg`);
				const payload = {
					parentId: selectedFile.id,
					files: [
						{
							name: downloadableMediaFile.name,
							size: downloadableMediaFile.size,
							// thumbnailName: downloadableMediaThumbnailFile.name,
							// thumbnailSize: downloadableMediaThumbnailFile.size
						}
					]
				}

				const uploadUrlData = await this.$store.dispatch('Projects/getDownloadableMediaUploadUrls', payload)
				// uploadPayloadsList.push(payload);

				uploadPromises.push({
						id: uploadUrlData[0].downloadableMedia.id,
						promise: this.$store.dispatch("Projects/uploadToS3", {
							url: uploadUrlData[0].downloadableMedia.presignedUrl,
							file: downloadableMediaFile,
						})
					}
				);

				// uploadPromises.push({
				// 		id: uploadUrlData[0].downloadableMediaThumbnail.id,
				// 		promise: this.$store.dispatch("Projects/uploadToS3", {
				// 			url: uploadUrlData[0].downloadableMediaThumbnail.presignedUrl,
				// 			file: downloadableMediaThumbnail,
				// 		})
				// 	}
				// );

				// console.log(downloadableMediaFile, downloadableMediaThumbnailFile)
				// await this.switchImage(selectedFile.bin_id, selectedFile);
            }

			await Promise.all(uploadPromises.map(uploadPromise => uploadPromise.promise));
			await this.$store.dispatch("Projects/updateFilesUploadStatus", uploadPromises.map(uploadPromise => uploadPromise.id));
			await this.$store.dispatch("Projects/refreshProjectTree");

			this.$snackbar(`Candidate successfully added to Downloads`, 'success');
			this.$store.dispatch("Loader/showProgressBar", false);
        },

		async removeFromDownloadsHandler(file) {
			this.$store.dispatch('UI/setRightColumn', 'project'); // workaround to hide the parent menu

			this.$store.dispatch("Loader/showProgressBar", "indeterminate");

			await this.$store.dispatch('Projects/deleteDownloadableMedia', file.id)
			await this.$store.dispatch("Projects/refreshProjectTree");

			this.$snackbar(`Candidate successfully removed from Downloads`, 'success');
			this.$store.dispatch("Loader/showProgressBar", false);

		}
    },
};
</script>

<style lang="scss">
#project-navigator-container {
    display: grid;
    align-content: start;
    background: transparent;
    color: #ababab;
    font-size: 1rem !important;
    position: relative;
    height: 100%;

    #project-root-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 22px;
        padding: 0 6px;
        margin-bottom: 10px;

        .project-title {
            font-size: 14px;
            text-transform: uppercase;
        }

        > i {
            cursor: pointer;
            margin-left: 15px;
        }
    }

    #import-media {
        display: none;
    }

    #project-tree {
        color: #ababab;
        overflow-y: scroll;

        &.--guest {
            // bin
            > .v-treeview-node {
                // bin children
                > .v-treeview-node__children {
                    // shared candidates
                    > .v-treeview-node {
                        + .v-treeview-node--leaf {
                            margin-top: 10px;
                        }

                        &.v-treeview-node--leaf {
                            > .v-treeview-node__root {
                                &:before {
                                    border-radius: 5px;
                                }
                            }

                            &[aria-expanded="false"] {
                                > .v-treeview-node__root {
                                    &:before {
                                        border-radius: 5px 5px 0 0;
                                    }
                                }
                            }
                        }

                        &[aria-expanded="false"] {
                            > .v-treeview-node__root {
                                &:before {
                                    border-radius: 0 0 5px 5px;
                                }
                            }
                        }

                        &.v-treeview-node--selected {
                            > .v-treeview-node__root {
                                &:before {
                                    background: #212143;
                                }
                            }
                        }

                        > .v-treeview-node__root {
                            position: relative;

                            &:before {
                                content: "";
                                display: block;
                                position: absolute;
                                left: 35px;
                                top: 0;
                                background: #202227;
                                width: calc(100% - 35px);
                                height: 100%;
                                opacity: 1;
                                right: initial;
                                bottom: initial;
                                transition: none;
                            }
                        }


                    }


                }
            }
        }

        // bin
        > .v-treeview-node {
             > .v-treeview-node__root {
                 .v-treeview-node__label {
                    font-size: 14px;
                     font-weight: bold;
                }
            }
            // bin children
            > .v-treeview-node__children {
                > .v-treeview-node--leaf {
                    //margin-left: 24px;
                }

                // sub-folder
                > .v-treeview-node {
                    // sub-folder children
                    > .v-treeview-node__children {
                        // file/candidate folder
                        > .v-treeview-node {
                            + .v-treeview-node--leaf {
                                margin-top: 10px;
                            }

                            &.v-treeview-node--leaf {
                                > .v-treeview-node__root {
                                    &:before {
                                        border-radius: 5px;
                                    }

                                    > .v-treeview-node__level:nth-child(1),
                                    > .v-treeview-node__level:nth-child(2) {
                                        width: initial;
                                    }
                                }

                                &[aria-expanded="false"] {
                                    > .v-treeview-node__root {
                                        &:before {
                                            border-radius: 5px 5px 0 0;
                                        }
                                    }
                                }
                            }

                            &[aria-expanded="false"] {
                                > .v-treeview-node__root {
                                    &:before {
                                        border-radius: 0 0 5px 5px;
                                    }
                                }
                            }

                            &.v-treeview-node--selected {
                                > .v-treeview-node__root {
                                    &:before {
                                        background: #212143;
                                    }
                                }
                            }

                            > .v-treeview-node__root {
                                position: relative;
                                margin-left: 24px;

                                &:before {
                                    content: "";
                                    display: block;
                                    position: absolute;
                                    left: 20px;
                                    top: 0;
                                    background: #202227;
                                    width: calc(100% - 20px);
                                    height: 100%;
                                    opacity: 1;
                                    right: initial;
                                    bottom: initial;
                                    transition: none;
                                }

                                > .v-treeview-node__level:nth-child(1) {
                                    width: initial;
                                }
                            }
                        }
                    }
                }

                // media folder
                > .v-treeview-node:first-of-type {
                    > .v-treeview-node__children {
                        // file/candidate folder
                        > .v-treeview-node {
                            // candidate folder children
                            > .v-treeview-node__children {
                                margin-bottom: 10px;

                                // candidate
                                > .v-treeview-node {
                                    > .v-treeview-node__root {
                                        position: relative;
                                        margin-left: 48px;

                                        &:before {
                                            content: "";
                                            display: block;
                                            position: absolute;
                                            left: -4px;
                                            top: 0;
                                            background: #202227;
                                            width: calc(100% + 4px);
                                            height: 100%;
                                            opacity: 1;
                                            right: initial;
                                            bottom: initial;
                                            transition: none;
                                        }

                                        > .v-treeview-node__level:nth-child(1),
                                        > .v-treeview-node__level:nth-child(2),
                                        > .v-treeview-node__level:nth-child(3) {
                                            width: initial;
                                        }
                                    }

                                    &.v-treeview-node--selected {
                                        > .v-treeview-node__root {
                                            &:before {
                                                background: #212143;
                                            }
                                        }
                                    }

                                    &:last-child {
                                        > .v-treeview-node__root {
                                            &::before {
                                                border-radius: 0 0 5px 5px;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // Archive and Trash
                > .v-treeview-node:nth-child(4),
                > .v-treeview-node:nth-child(5) {
                    > .v-treeview-node__children {
                        // sub-folder
                        > .v-treeview-node {
                            > .v-treeview-node__root {
                                &:before {
                                    background: transparent;
                                }
                            }

                            > .v-treeview-node__children {
                                margin-bottom: 10px;

                                // file
                                > .v-treeview-node {
                                    + .v-treeview-node--leaf {
                                        margin-top: 10px;
                                    }

                                    &.v-treeview-node--leaf {
                                        margin-left: 48px;

                                        > .v-treeview-node__root {
                                            &:before {
                                                border-radius: 5px;
                                                left: 0;
                                                width: 100%;
                                            }

                                            > .v-treeview-node__level:nth-child(1),
                                            > .v-treeview-node__level:nth-child(2),
                                            > .v-treeview-node__level:nth-child(3) {
                                                width: initial;
                                            }
                                        }

                                        &[aria-expanded="false"] {
                                            > .v-treeview-node__root {
                                                &:before {
                                                    border-radius: 5px 5px 0 0;
                                                }
                                            }
                                        }
                                    }

                                    &.v-treeview-node--selected {
                                        > .v-treeview-node__root {
                                            &:before {
                                                background: #212143;
                                            }
                                        }
                                    }

                                    > .v-treeview-node__root {
                                        position: relative;

                                        &:before {
                                            content: "";
                                            display: block;
                                            position: absolute;
                                            left: 50px;
                                            top: 0;
                                            background: #202227;
                                            width: calc(100% - 50px);
                                            height: 100%;
                                            opacity: 1;
                                            right: initial;
                                            bottom: initial;
                                            transition: none;
                                        }
                                    }
                                }
                            }

                        }
                    }
                }

            }
        }

        .v-treeview-node__root:before {
            display: none;
        }

        .v-icon {
            color: #989898;
        }

        .v-treeview-node__checkbox {
            display: none;
        }

        .v-treeview-node__level {
            //width: initial;
        }
    }

    .file {
        position: relative;
        overflow: visible;

        .loading-spinner-container {
            position: absolute;
            left: -40px;
            top: 50%;
            transform: translateY(-50%);
        }
    }

    .file {
        display: flex;
        align-items: center;

        .file-name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            width: 100%;
            line-height: 35px;
            // font-weight: bold;
            &.--selected {
                color: #ffffff;
                font-weight: bold;
            }
        }
    }

    .v-treeview-node__label {
        // overflow: visible;
        line-height: 1;

        > span {
            // font-weight: bold;
        }

        .v-input {
            font-size: 12px;

            .v-input__control {
                min-height: initial;
                // height: 10px;
                input {
                    line-height: 1;
                    color: var(--font-default-color);
                    padding: 0;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    white-space: nowrap;
                }

                .v-input__slot {
                    padding: 0;
                    background: transparent;
                }
            }

            &--is-focused {
                .v-input__slot {
                    // background: #363636;
                    background: transparent;
                }
            }
        }
    }

    .v-treeview-node__root {
        min-height: 25px;

        padding: 0;

        &:hover .menu-container {
            opacity: 1;
        }
    }

    .menu-container {
        opacity: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .v-icon.v-icon:after {
        display: none;
    }

    .v-treeview-node__content {
        margin-left: -3px;
        z-index: 1;
    }

    .v-treeview-node__prepend {
        margin-right: 0;
        margin-left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
    }

    .v-treeview-node__append {
        min-width: 24px;
    }

    i {
        font-size: 18px;
        cursor: pointer;
    }

    .thumbnail-container {
        width: 50px;
        height: 35px;
        margin-left: 4px;
        margin-right: 5px;

        &.--edited,
        &.--notify {
            &::before {
                content: "";
                display: block;
                position: absolute;
                top: 7px;
                left: -10px;
                width: 5px;
                height: 5px;
                background: var(--font-default-color);
                border-radius: 50%;
            }

            &.--selected::before {
                background: var(--primary);
            }
        }

        &.--notify::before {
            background: var(--primary);
        }

        .file-thumbnail {
            width: 100%;
            height: 100%;
            max-height: 35px;
            object-fit: contain;
            padding: 3px 0;
            // border-radius: var(--thumbnail-sm-border-radius);
        }
    }

    .placeholder-container {
        height: 35px;
        padding: 3px 0;

        .placeholder-preview {
            height: 100%;
            border-radius: 1px;
        }
    }
}

.file-options-container,
.file-options-container--no-icons {
    z-index: 200 !important;
    border-radius: var(--box-rounding);

    &.--level2 {
        //min-width: initial !important;

        &.--rating {
            .v-list-item {
                display: flex !important;
                align-items: center;
            }

            .v-list-item__title {
                margin-left: 0 !important;
            }
        }
    }

    &.--overflow {
        padding: 25px 0;
        background: var(--dark-gray);

        .v-list {
            height: 340px;
            padding: 0;
            overflow-y: scroll;
            position: relative;
            display: flex;
            flex-direction: column;

            scrollbar-color: #989898 transparent;
            scrollbar-width: thin;

            &::-webkit-scrollbar {
                width: 5px;
            }

            &::-webkit-scrollbar-thumb {
                background-color: #989898;
                border-radius: 50px;
            }
        }
    }

    .v-list {
        background: var(--dark-gray);
        padding: 25px 0;

        .v-list-item {
            min-height: 20px;
            padding: 0 25px;
            display: grid;
            grid-template-columns: 18px 1fr auto auto;
            cursor: pointer;
            position: relative;

            &.sub-menu-activator {
                grid-template-columns: 18px 1fr auto;
            }

            ~ .v-list-item {
                margin-top: 2px;
            }

            i:last-of-type {
                //margin-left: 1rem;
            }

            &.--first {
                order: -1;
            }

            > * {
                z-index: 2;
            }

            &:before {
                display: none;
            }

            &:hover:after {
                content: '';
                position: absolute;
                display: flex;
                width: calc(100% - 20px);
                height: calc(100%);
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background: rgb(54, 54, 54);
                border-radius: 5px;
                z-index: 1;
            }

            &.full-span {
                grid-template-columns: 1fr;
            }

            &.stars-icons-container {
                grid-template-columns: repeat(5, 1fr);
            }

            &[aria-expanded="true"] {
                //background: transparent;
            }

            &.--selected {
                .material-icons,
                .v-list-item__title {
                    color: var(--primary);
                }
            }

            &.v-list-item--disabled {
                opacity: 0.6;
            }

            .material-icons, .material-symbols-outlined {
                color: #989898;
                font-size: 18px;
                width: 18px;

                &.arrow-left {
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                }
            }

            .v-list-item__title {
                margin-left: 4px;
                margin-right: 4px;
                color: #989898;
                font-size: 1rem;
                line-height: 2;
                display: flex;
                align-items: center;
            }

            &:after {
                display: none;
            }
        }
    }

    &--no-icons {
        .v-list {
            .v-list-item {
                grid-template-columns: 1fr;
            }
        }
    }


    .control-label--new {
        color: white;
        background: var(--color-accent);
        border-radius: 5px;
        padding: 3px 8px;
        font-size: 0.8rem;
        margin-left: 10px;
    }
}
</style>
